/**
 * AdminDocumentation.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getIcon } from '../utils/icons';
import HelpPopup from './HelpPopup';
import './AdminDocumentation.css';

const AdminDocumentation = () => {
  const [activeTab, setActiveTab] = useState('technical');
  const [activeSection, setActiveSection] = useState('overview');

  // Technical Documentation Sections
  const technicalSections = [
    {
      id: 'overview',
      title: 'System Overview',
      icon: '📋'
    },
    {
      id: 'architecture',
      title: 'Architecture',
      icon: '🏗️'
    },
    {
      id: 'database',
      title: 'Database Schema',
      icon: '🗄️'
    },
    {
      id: 'api',
      title: 'API Reference',
      icon: '🔌'
    },
    {
      id: 'infrastructure',
      title: 'Infrastructure',
      icon: '☁️'
    },
    {
      id: 'deployment',
      title: 'Deployment',
      icon: '🚀'
    },
    {
      id: 'features',
      title: 'Features',
      icon: '✨'
    },
    {
      id: 'security',
      title: 'Security',
      icon: '🔒'
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: '🔧'
    },
    {
      id: 'docker',
      title: 'Docker Infrastructure',
      icon: '🐳'
    },
    {
      id: 'postgres-migration',
      title: 'PostgreSQL Migration',
      icon: '🔄'
    },
    {
      id: 'mail-server',
      title: 'Mail Server (DMS)',
      icon: '📧'
    },
    {
      id: 'developer',
      title: 'About the Developer',
      icon: '👨‍💻'
    },
    {
      id: 'version-history',
      title: 'Version History',
      icon: '📅'
    }
  ];

  // End User Documentation Sections
  const endUserSections = [
    {
      id: 'getting-started',
      title: 'Getting Started Guide',
      icon: '🚀'
    },
    {
      id: 'order-processing',
      title: 'Order Processing',
      icon: '📦'
    },
    {
      id: 'mail-orders',
      title: 'Mail / Check Orders',
      icon: '✉️'
    },
    {
      id: 'payment-fees',
      title: 'Payment Fees (Tax)',
      icon: '🧾'
    },
    {
      id: 'refunds',
      title: 'Processing Refunds',
      icon: '💵'
    },
    {
      id: 'payment-dashboards',
      title: 'Payment Dashboards',
      icon: '💳'
    },
    {
      id: 'order-status',
      title: 'Order Status Management',
      icon: '📋'
    },
    {
      id: 'product-creation',
      title: 'Creating Products',
      icon: '🛍️'
    },
    {
      id: 'tune-creation',
      title: 'Tune Page Creation',
      icon: '🎵'
    },
    {
      id: 'asset-upload',
      title: 'Asset Upload',
      icon: '📁'
    },
    {
      id: 'abc-tunes-display',
      title: 'ABC Tunes Display',
      icon: '🎼'
    },
    {
      id: 'shipping-config',
      title: 'Shipping Configuration',
      icon: '🚚'
    },
    {
      id: 'page-creation',
      title: 'Page Creation & Management',
      icon: '📄'
    },
    {
      id: 'article-creation',
      title: 'How to Create an Article',
      icon: '✍️'
    },
    {
      id: 'customer-management',
      title: 'Customer Management',
      icon: '👥'
    },
    {
      id: 'inventory-management',
      title: 'Inventory Management',
      icon: '📊'
    },
    {
      id: 'order-reminders',
      title: 'Order Reminder Emails',
      icon: '📨'
    }
  ];

  // Get current sections based on active tab
  const currentSections = activeTab === 'technical' ? technicalSections : endUserSections;

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Set default section for each tab
    if (tab === 'technical') {
      setActiveSection('overview');
    } else {
      setActiveSection('getting-started');
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      // Technical Documentation
      case 'overview':
        return <OverviewSection />;
      case 'architecture':
        return <ArchitectureSection />;
      case 'database':
        return <DatabaseSection />;
      case 'api':
        return <ApiSection />;
      case 'infrastructure':
        return <InfrastructureSection />;
      case 'deployment':
        return <DeploymentSection />;
      case 'features':
        return <FeaturesSection />;
      case 'security':
        return <SecuritySection />;
      case 'troubleshooting':
        return <TroubleshootingSection />;
      case 'docker':
        return <DockerSection />;
      case 'postgres-migration':
        return <PostgresMigrationSection />;
      case 'mail-server':
        return <MailServerSection />;
      case 'developer':
        return <DeveloperSection />;
      case 'version-history':
        return <VersionHistorySection />;
      
      // End User Documentation
      case 'getting-started':
        return <GettingStartedSection />;
      case 'order-processing':
        return <OrderProcessingSection />;
      case 'mail-orders':
        return <MailOrderSection />;
      case 'payment-fees':
        return <PaymentFeesTaxExportSection />;
      case 'refunds':
        return <RefundsSection />;
      case 'payment-dashboards':
        return <PaymentDashboardsSection />;
      case 'order-status':
        return <OrderStatusSection />;
      case 'tune-creation':
        return <TuneCreationSection />;
      case 'asset-upload':
        return <AssetUploadSection />;
      case 'abc-tunes-display':
        return <ABCTunesDisplaySection />;
      case 'product-creation':
        return <ProductCreationSection />;
      case 'shipping-config':
        return <ShippingConfigSection />;
      case 'page-creation':
        return <PageCreationSection />;
      case 'article-creation':
        return <ArticleCreationSection />;
      case 'customer-management':
        return <CustomerManagementSection />;
      case 'inventory-management':
        return <InventoryManagementSection />;
      case 'order-reminders':
        return <OrderRemindersSection />;
      default:
        return <OverviewSection />;
    }
  };

  return (
    <div className="admin-documentation-container container">
      <div className="admin-documentation-content content-card">
        <div className="admin-documentation-header">
          <div className="header-title-section">
            <h2>
              Documentation
              <HelpPopup title="About This Documentation" position="bottom">
                <p><strong>This section contains comprehensive documentation</strong> for system administrators, developers, and end users.</p>
                
                <p><strong>What's Included:</strong></p>
                <ul>
                  <li><strong>Technical Documentation:</strong> Architecture, database schema, API reference, infrastructure, and deployment</li>
                  <li><strong>End User Guides:</strong> Step-by-step guides for order processing, refunds, product creation, tune management, and more</li>
                  <li><strong>Administrative Guides:</strong> How to manage orders, configure shipping, upload assets, and verify transactions</li>
                </ul>
                
                <div className="tip">
                  💡 Use this documentation to understand, maintain, and operate the system
                </div>
              </HelpPopup>
            </h2>
            <p className="header-subtitle">Complete guides for technical implementation and daily operations</p>
          </div>
          <Link to="/admin" className="btn btn-outline">
            {getIcon('back')} Back to Admin
          </Link>
        </div>

        {/* Tab Navigation */}
        <div className="documentation-tabs">
          <button
            className={`tab-button ${activeTab === 'technical' ? 'active' : ''}`}
            onClick={() => handleTabChange('technical')}
          >
            🔧 Technical Documentation
          </button>
          <button
            className={`tab-button ${activeTab === 'enduser' ? 'active' : ''}`}
            onClick={() => handleTabChange('enduser')}
          >
            📚 End User Documentation
          </button>
        </div>

        <div className="documentation-layout">
          {/* Sidebar Navigation */}
          <nav className="documentation-sidebar">
            <div className="sidebar-title">
              {activeTab === 'technical' ? 'Technical Topics' : 'User Guides'}
            </div>
            {currentSections.map(section => (
              <button
                key={section.id}
                className={`sidebar-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="sidebar-icon">{section.icon}</span>
                <span className="sidebar-text">{section.title}</span>
              </button>
            ))}
          </nav>

          {/* Main Content Area */}
          <div className="documentation-main">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Section Components
const OverviewSection = () => (
  <div className="doc-section">
    <h2>📋 System Overview</h2>
    
    <div className="doc-card">
      <h3>Application Purpose</h3>
      <p>Cranford Publications is a full-stack e-commerce platform specialized in Celtic fiddle music, featuring:</p>
      <ul>
        <li>Traditional Irish, Scottish, and Cape Breton fiddle music</li>
        <li>Books, CDs, sheet music, and educational materials</li>
        <li>Interactive tune notation with audio samples</li>
        <li>Historical articles and cultural content</li>
        <li>Wholesale and retail sales capabilities</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Technology Stack</h3>
      
      <h4>Frontend</h4>
      <ul>
        <li><strong>React 18.2.0:</strong> UI framework</li>
        <li><strong>React Router 6.30.1:</strong> Client-side routing</li>
        <li><strong>TipTap Editor:</strong> Rich text editing</li>
        <li><strong>Custom Theme System:</strong> Dark, Light, Ocean, Forest, Sunset, Midnight themes — all admin pages are fully theme-aware</li>
        <li><strong>Context API:</strong> State management (Auth, Cart, Theme, Visibility)</li>
      </ul>

      <h4>Backend</h4>
      <ul>
        <li><strong>Node.js + Express:</strong> Web server and API</li>
        <li><strong>SQLite3:</strong> Database (file-based, portable)</li>
        <li><strong>JWT:</strong> Authentication tokens</li>
        <li><strong>bcrypt:</strong> Password hashing</li>
        <li><strong>Multer:</strong> File upload handling</li>
        <li><strong>Nodemailer:</strong> Email notifications</li>
      </ul>

      <h4>Payment Processing</h4>
      <ul>
        <li><strong>Stripe:</strong> Credit card processing</li>
        <li><strong>PayPal:</strong> Alternative payment method</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Development Ports</h3>
      <div className="code-block">
        <div><strong>Frontend:</strong> http://localhost:3000</div>
        <div><strong>Backend API:</strong> http://localhost:4000</div>
        <div><strong>Proxy:</strong> /api/* requests forwarded to :4000</div>
      </div>
    </div>

    <div className="doc-card important">
      <h3>🔑 Key Features</h3>
      <div className="feature-grid">
        <div className="feature-item">
          <h4>🛒 E-Commerce</h4>
          <p>Full shopping cart, checkout, Stripe/PayPal integration</p>
        </div>
        <div className="feature-item">
          <h4>👥 User Management</h4>
          <p>Role-based access control (Admin, Content Creator, Wholesale, Retail)</p>
        </div>
        <div className="feature-item">
          <h4>📝 Content Management</h4>
          <p>Dynamic pages, tune notation, articles with rich text editing</p>
        </div>
        <div className="feature-item">
          <h4>🎵 Asset Management</h4>
          <p>Upload, organize, and serve images, audio, and documents</p>
        </div>
        <div className="feature-item">
          <h4>📊 Order Management</h4>
          <p>Order tracking, status updates, and automated reminder emails for pending orders</p>
        </div>
        <div className="feature-item">
          <h4>💰 Wholesale System</h4>
          <p>Tiered pricing, bulk discounts, wholesale-only access</p>
        </div>
      </div>
    </div>
  </div>
);

const ArchitectureSection = () => (
  <div className="doc-section">
    <h2>🏗️ System Architecture</h2>
    
    <div className="doc-card">
      <h3>Development Flow</h3>
      <pre className="architecture-diagram">
{`┌─────────────────────────────────────────────────────────┐
│                    Browser Client                        │
│                http://localhost:3000                     │
└──────────────────────┬──────────────────────────────────┘
                       │
                       ▼
┌─────────────────────────────────────────────────────────┐
│              React Dev Server (Port 3000)                │
│  Components: Header, Shop, Admin, Pages, etc.           │
│  Context: Auth, Cart, Visibility, Theme                 │
│  Routing: React Router (client-side)                    │
└──────────────────────┬──────────────────────────────────┘
                       │ Proxy: /api/* → :4000
                       ▼
┌─────────────────────────────────────────────────────────┐
│            Express Backend (Port 4000)                   │
│  Routes: /api/products, /api/content, /api/users, etc.  │
│  Auth: JWT + bcrypt  │  File uploads: Multer            │
│  Email: Nodemailer   │  Payments: Stripe, PayPal        │
└──────────────────────┬──────────────────────────────────┘
                       │
                       ▼
┌─────────────────────────────────────────────────────────┐
│         SQLite Database (cranford_publications.db)       │
│  Tables: products, users, orders, content_pages, etc.   │
└─────────────────────────────────────────────────────────┘`}
      </pre>
    </div>

    <div className="doc-card">
      <h3>Production Architecture (cranfordpub.ca)</h3>
      <pre className="architecture-diagram">
{`Internet (HTTPS 443 / HTTP 80)
        │
        ▼
┌───────────────────┐
│      Traefik      │  Reverse proxy + automatic Let's Encrypt SSL
│  Port 80 / 443    │  Routes by hostname → container
└────────┬──────────┘
         │
         ▼
┌────────────────────────────────────┐
│   cranford-app Docker Container    │
│   Image: cranfordpubweb:latest     │
│                                    │
│  ┌──────────────────────────────┐  │
│  │  Express (Port 4000)        │  │
│  │  ├─ Serves React build      │  │
│  │  ├─ /api/* routes           │  │
│  │  └─ /images, /mp3s, etc.   │  │
│  └──────────────────────────┬──┘  │
│                             │      │
│  ┌──────────────────────────▼──┐  │
│  │  SQLite DB                  │  │
│  │  cranford_publications.db   │  │
│  └──────────────────────────┘  │  │
└────────────────────────────────────┘

Other services on VPS:
  • Traefik (traefik.cranfordpub.ca)
  • Docker Mail Server (mail.cranfordpub.ca)
  • Rainloop Webmail (webmail.cranfordpub.ca)
  • PostgreSQL + pgAdmin (future/dev)
  • Node-RED automation
  • Portainer (container management UI)`}
      </pre>
    </div>

    <div className="doc-card">
      <h3>Directory Structure</h3>
      <pre className="code-block">
{`PaulsNewWebSite-V3/
├── frontend/
│   ├── public/               # CRA shell + PWA files
│   │   ├── index.html        # Shell HTML (version meta, SW registration)
│   │   ├── manifest.json     # PWA manifest (name, version, icons)
│   │   └── sw.js             # Service worker (offline caching)
│   ├── src/
│   │   ├── components/       # React components (Admin*, Page*, etc.)
│   │   ├── context/          # Context providers (Auth, Cart, Theme, Visibility)
│   │   ├── hooks/            # Custom React hooks
│   │   ├── utils/            # Utility functions
│   │   ├── constants/        # Shared constants (e.g. tuneTimeSignatures)
│   │   ├── styles/           # Shared CSS (admin-shared.css, etc.)
│   │   ├── App.js            # Main app + React Router setup
│   │   └── index.js          # Entry point
│   └── package.json          # Frontend dependencies
│
├── backend/
│   ├── routes/               # Express API route handlers
│   │   ├── products.js       # Product endpoints
│   │   ├── users.js          # User/auth endpoints
│   │   ├── content.js        # Content pages endpoints
│   │   ├── assets.js         # Asset management
│   │   ├── orders.js         # Order management
│   │   ├── tuneMetadata.js   # Tune staging/enrichment endpoints
│   │   └── newsletter.js     # Newsletter endpoints
│   ├── middleware/
│   │   └── auth.js           # JWT authentication middleware
│   ├── utils/
│   │   └── tuneTimeSignatures.js  # Shared time signature logic
│   ├── public/               # Static assets served by Express
│   │   ├── index.html        # Production SPA shell
│   │   ├── images/           # Product images
│   │   └── mp3s/             # Audio files
│   ├── uploads/              # User-uploaded files (multer)
│   ├── server.js             # Express server entry point
│   ├── cranford_publications.db  # SQLite production DB
│   └── package.json          # Backend dependencies
│
├── Docker/
│   ├── Dockerfile            # Unified container build
│   ├── docker-compose-traefik.yml  # Production compose (Traefik SSL)
│   ├── deploy-X.Y.Z.sh       # Release deploy scripts
│   └── sync-db-to-vps.sh     # DB sync utility
│
└── NewDocumentation/         # Developer & release docs`}
      </pre>
    </div>

    <div className="doc-card">
      <h3>Component Hierarchy</h3>
      <pre className="code-block">
{`App
├── Header
│   ├── Navigation
│   ├── SearchBar
│   ├── CartIcon
│   └── UserMenu
├── Router
│   ├── Home
│   ├── Shop (Product List)
│   ├── ProductDetail
│   ├── Checkout
│   ├── Profile
│   ├── Admin Dashboard
│   │   ├── AdminProducts
│   │   ├── AdminUsers
│   │   ├── AdminOrders
│   │   ├── AdminPages
│   │   ├── AdminAssets
│   │   ├── AdminSettings
│   │   └── AdminDocumentation (this page)
│   └── Content Pages
│       ├── PageViewer
│       ├── Article
│       └── Tunes
├── CartPanel
└── Footer`}
      </pre>
    </div>

    <div className="doc-card">
      <h3>Data Flow</h3>
      <ol>
        <li><strong>User Action:</strong> User clicks button or submits form</li>
        <li><strong>Component Handler:</strong> React component handles event</li>
        <li><strong>API Request:</strong> Fetch/Axios call to backend endpoint</li>
        <li><strong>Authentication:</strong> JWT token sent in Authorization header</li>
        <li><strong>Backend Processing:</strong> Express route handler processes request</li>
        <li><strong>Database Query:</strong> SQLite query executed</li>
        <li><strong>Response:</strong> JSON data returned to frontend</li>
        <li><strong>State Update:</strong> React state/context updated</li>
        <li><strong>Re-render:</strong> UI updates to reflect new data</li>
      </ol>
    </div>
  </div>
);

const DatabaseSection = () => (
  <div className="doc-section">
    <h2>🗄️ Database Schema</h2>
    
    <div className="doc-card">
      <h3>Database File Location</h3>
      <div className="code-block">
        <strong>Development:</strong> backend/cranford_publications.db<br/>
        <strong>Production:</strong> /app/backend/cranford_publications.db (Docker)
      </div>
    </div>

    <div className="doc-card">
      <h3>Core Tables</h3>
      
      <h4>users</h4>
      <pre className="code-block">
{`CREATE TABLE users (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  username TEXT UNIQUE NOT NULL,
  email TEXT UNIQUE NOT NULL,
  password TEXT NOT NULL,           -- bcrypt hashed
  roles TEXT DEFAULT 'customer',    -- JSON array: ["customer", "admin"]
  first_name TEXT,
  last_name TEXT,
  phone TEXT,
  shipping_address TEXT,
  city TEXT,
  province TEXT,
  postal_code TEXT,
  country TEXT,
  newsletter BOOLEAN DEFAULT 0,
  is_active BOOLEAN DEFAULT 1,
  email_verified BOOLEAN DEFAULT 0,
  created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
  updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
);`}
      </pre>

      <h4>products</h4>
      <pre className="code-block">
{`CREATE TABLE products (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  name TEXT NOT NULL,
  type TEXT NOT NULL,               -- Category: Book, CD, etc.
  price REAL NOT NULL,
  inventory INTEGER DEFAULT 0,
  description TEXT,
  image TEXT,                       -- Path or asset ID
  author TEXT,
  artist TEXT,
  category TEXT,
  sku TEXT UNIQUE,
  tags TEXT,                        -- Comma-separated
  related_products TEXT,            -- Comma-separated product IDs
  related_include_explicit BOOLEAN DEFAULT 1,
  related_include_tags BOOLEAN DEFAULT 1,
  related_include_category BOOLEAN DEFAULT 1,
  shipping_enabled BOOLEAN DEFAULT 1,
  minimum_shipping REAL,
  wholesale_pricing TEXT,           -- JSON object
  created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
  updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
);`}
      </pre>

      <h4>orders</h4>
      <pre className="code-block">
{`CREATE TABLE orders (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  user_id INTEGER,
  order_number TEXT UNIQUE NOT NULL,
  items TEXT NOT NULL,              -- JSON array of products
  subtotal REAL NOT NULL,
  tax REAL NOT NULL,
  shipping REAL NOT NULL,
  total REAL NOT NULL,
  status TEXT DEFAULT 'pending',    -- pending, processing, shipped, delivered
  payment_method TEXT,              -- stripe, paypal
  payment_intent_id TEXT,
  shipping_address TEXT,
  tracking_number TEXT,
  notes TEXT,
  created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
  updated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
  FOREIGN KEY (user_id) REFERENCES users(id)
);`}
      </pre>

      <h4>content_pages</h4>
      <pre className="code-block">
{`CREATE TABLE content_pages (
  page_id TEXT PRIMARY KEY,         -- URL-friendly: "silver-apple-jig"
  title TEXT NOT NULL,
  description TEXT,
  type TEXT DEFAULT 'general',      -- tune-notation, article, product
  metadata TEXT,                    -- JSON object (artist, tune_type, etc.)
  tags TEXT,                        -- Comma-separated for SEO
  created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
  updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
);`}
      </pre>

      <h4>page_content</h4>
      <pre className="code-block">
{`CREATE TABLE page_content (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  page_id TEXT NOT NULL,
  html TEXT NOT NULL,
  version INTEGER DEFAULT 1,
  created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
  created_by INTEGER,
  FOREIGN KEY (page_id) REFERENCES content_pages(page_id),
  FOREIGN KEY (created_by) REFERENCES users(id)
);`}
      </pre>

      <h4>assets</h4>
      <pre className="code-block">
{`CREATE TABLE assets (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  original_filename TEXT NOT NULL,
  stored_filename TEXT UNIQUE NOT NULL,
  file_path TEXT NOT NULL,
  file_type TEXT NOT NULL,          -- image, music, video, document
  mime_type TEXT,
  file_size INTEGER,
  category TEXT,
  subcategory TEXT,
  description TEXT,
  alt_text TEXT,
  tags TEXT,                        -- Comma-separated
  is_public BOOLEAN DEFAULT 1,
  uploaded_by INTEGER,
  created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
  FOREIGN KEY (uploaded_by) REFERENCES users(id)
);`}
      </pre>
    </div>

    <div className="doc-card">
      <h3>Relationships</h3>
      <ul>
        <li><strong>users → orders:</strong> One-to-many (user can have multiple orders)</li>
        <li><strong>users → assets:</strong> One-to-many (user uploads multiple assets)</li>
        <li><strong>content_pages → page_content:</strong> One-to-many (versioning)</li>
        <li><strong>products → products:</strong> Many-to-many via related_products (self-referencing)</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>⚠️ Important Notes</h3>
      <ul>
        <li><strong>JSON Fields:</strong> roles, metadata, items, wholesale_pricing stored as JSON strings</li>
        <li><strong>Comma-Separated:</strong> tags, related_products stored as comma-separated values</li>
        <li><strong>File Paths:</strong> Assets stored in backend/uploads/, referenced by ID in database</li>
        <li><strong>Soft Deletes:</strong> Use is_active flag instead of DELETE for users</li>
      </ul>
    </div>
  </div>
);

const ApiSection = () => (
  <div className="doc-section">
    <h2>🔌 API Reference</h2>
    
    <div className="doc-card">
      <h3>Base URL</h3>
      <div className="code-block">
        <strong>Development:</strong> http://localhost:4000/api<br/>
        <strong>Production:</strong> https://yourdomain.com/api
      </div>
    </div>

    <div className="doc-card">
      <h3>Authentication</h3>
      <p>Most endpoints require JWT authentication. Include token in Authorization header:</p>
      <div className="code-block">
        Authorization: Bearer &lt;your-jwt-token&gt;
      </div>
    </div>

    <div className="api-endpoint-group">
      <h3>Products API</h3>
      
      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/products</span>
        </div>
        <p>Get all products with optional filtering</p>
        <div className="endpoint-details">
          <strong>Query Parameters:</strong>
          <ul>
            <li><code>category</code> - Filter by product type</li>
            <li><code>search</code> - Search by name, author, artist</li>
            <li><code>tags</code> - Filter by tags</li>
          </ul>
          <strong>Response:</strong> Array of product objects
        </div>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/products/:id</span>
        </div>
        <p>Get single product by ID with related products</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/products</span>
        </div>
        <p>Create new product (Admin only)</p>
        <div className="endpoint-details">
          <strong>Body:</strong>
          <pre className="code-block">{`{
  "name": "Product Name",
  "type": "Book",
  "price": 29.99,
  "inventory": 100,
  "description": "Product description",
  "sku": "PROD-001"
}`}</pre>
        </div>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method put">PUT</span>
          <span className="path">/api/products/:id</span>
        </div>
        <p>Update product (Admin only)</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method delete">DELETE</span>
          <span className="path">/api/products/:id</span>
        </div>
        <p>Delete product (Admin only)</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/products/:id/export</span>
        </div>
        <p>Export single product as ZIP (Admin only)</p>
        <div className="endpoint-details">
          <strong>Response:</strong> ZIP file containing product.json and image files
        </div>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/products/export/bulk</span>
        </div>
        <p>Export multiple products as ZIP (Admin only)</p>
        <div className="endpoint-details">
          <strong>Body:</strong> <code>{"{ productIds: [1, 2, 3] }"}</code><br/>
          <strong>Response:</strong> ZIP file containing all selected products
        </div>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/products/import/zip</span>
        </div>
        <p>Import product from ZIP (Admin only)</p>
        <div className="endpoint-details">
          <strong>Body:</strong> multipart/form-data with zipfile<br/>
          <strong>Response:</strong> Import result with conflict resolution
        </div>
      </div>
    </div>

    <div className="api-endpoint-group">
      <h3>Users & Authentication API</h3>
      
      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/users/register</span>
        </div>
        <p>Register new user</p>
        <div className="endpoint-details">
          <strong>Body:</strong>
          <pre className="code-block">{`{
  "username": "johndoe",
  "email": "john@example.com",
  "password": "[your-password]",
  "firstName": "John",
  "lastName": "Doe"
}`}</pre>
        </div>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/users/login</span>
        </div>
        <p>Login and receive JWT token</p>
        <div className="endpoint-details">
          <strong>Response:</strong>
          <pre className="code-block">{`{
  "token": "eyJhbGciOiJIUzI1NiIs...",
  "user": {
    "id": 1,
    "username": "johndoe",
    "roles": ["customer"]
  }
}`}</pre>
        </div>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/users/me</span>
        </div>
        <p>Get current user profile (requires auth)</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/users</span>
        </div>
        <p>Get all users (Admin only)</p>
      </div>
    </div>

    <div className="api-endpoint-group">
      <h3>Content Pages API</h3>
      
      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/content/pages</span>
        </div>
        <p>Get all pages</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/content/pages/:pageId</span>
        </div>
        <p>Get page with current content version</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/content/pages</span>
        </div>
        <p>Create new page (Admin/Content Creator)</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method put">PUT</span>
          <span className="path">/api/content/pages/:pageId</span>
        </div>
        <p>Update page metadata</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/content/pages/:pageId/content</span>
        </div>
        <p>Save new content version</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/content/pages/export/bulk</span>
        </div>
        <p>Export multiple pages as ZIP (Admin/Content Creator)</p>
        <div className="endpoint-details">
          <strong>Body:</strong> <code>{"{ pageIds: ['page-id-1', 'page-id-2'] }"}</code><br/>
          <strong>Response:</strong> ZIP file with pages and assets
        </div>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/content/pages/import/bulk</span>
        </div>
        <p>Import pages from ZIP (Admin/Content Creator)</p>
        <div className="endpoint-details">
          <strong>Body:</strong> multipart/form-data with zipfile<br/>
          <strong>Response:</strong> Import summary (pages imported/updated, assets created)
        </div>
      </div>
    </div>

    <div className="api-endpoint-group">
      <h3>Assets API</h3>
      
      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/assets/upload</span>
        </div>
        <p>Upload new asset (multipart/form-data)</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/assets/:id/download</span>
        </div>
        <p>Download/serve asset file</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/assets/:id/thumbnail</span>
        </div>
        <p>Get thumbnail for image assets</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/assets/export/bulk</span>
        </div>
        <p>Export multiple assets as ZIP (Admin only)</p>
        <div className="endpoint-details">
          <strong>Body:</strong> <code>{"{ assetIds: [1, 2, 3] }"}</code><br/>
          <strong>Response:</strong> ZIP file with assets and manifest.json
        </div>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/assets/import/bulk</span>
        </div>
        <p>Import assets from ZIP (Admin only)</p>
        <div className="endpoint-details">
          <strong>Body:</strong> multipart/form-data with zipfile<br/>
          <strong>Response:</strong> Import summary (imported/updated counts)
        </div>
      </div>
    </div>

    <div className="api-endpoint-group">
      <h3>Orders API</h3>
      
      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method post">POST</span>
          <span className="path">/api/orders</span>
        </div>
        <p>Create new order</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method get">GET</span>
          <span className="path">/api/orders</span>
        </div>
        <p>Get all orders (Admin) or user's orders</p>
      </div>

      <div className="api-endpoint">
        <div className="endpoint-header">
          <span className="method put">PUT</span>
          <span className="path">/api/orders/:id</span>
        </div>
        <p>Update order status (Admin only)</p>
      </div>
    </div>
  </div>
);

const InfrastructureSection = () => (
  <div className="doc-section">
    <h2>☁️ Infrastructure</h2>
    
    <div className="doc-card">
      <h3>Server Requirements</h3>
      <ul>
        <li><strong>Node.js:</strong> v16 or higher</li>
        <li><strong>npm:</strong> v8 or higher</li>
        <li><strong>RAM:</strong> Minimum 1GB, recommended 2GB+</li>
        <li><strong>Storage:</strong> Minimum 10GB (database and uploads grow over time)</li>
        <li><strong>OS:</strong> Linux (Ubuntu/Debian recommended), macOS, Windows</li>
      </ul>
    </div>

    <div className="doc-card warning">
      <h3>Environment Variables</h3>
      <p>Create a <code>.env</code> file in the backend directory. <strong>Never commit this file to git</strong> — it contains secrets. The values below are placeholders only; replace every <code>[…]</code> with your real credentials.</p>
      <pre className="code-block">{`# Server Configuration
PORT=4000
NODE_ENV=production
FRONTEND_URL=https://yourdomain.com

# Database
DATABASE_PATH=./cranford_publications.db

# JWT Configuration — use a long random string, never commit the real value
JWT_SECRET=[minimum 32 char random string — use: openssl rand -hex 32]
JWT_EXPIRES_IN=7d

# Email Configuration (for order notifications)
EMAIL_HOST=smtp.gmail.com
EMAIL_PORT=587
EMAIL_USER=[your-email@gmail.com]
EMAIL_PASS=[your Gmail App Password — not your login password]
EMAIL_FROM=noreply@cranfordpub.com

# Payment Configuration
STRIPE_SECRET_KEY=[sk_live_... from Stripe Dashboard]
STRIPE_PUBLISHABLE_KEY=[pk_live_... from Stripe Dashboard]
PAYPAL_CLIENT_ID=[your PayPal client ID]
PAYPAL_SECRET=[your PayPal secret]

# File Upload
MAX_FILE_SIZE=10485760  # 10MB in bytes
UPLOAD_DIR=./uploads`}</pre>
    </div>

    <div className="doc-card">
      <h3>Docker Setup (Optional)</h3>
      <p>The application can run in Docker containers for easy deployment.</p>
      
      <h4>docker-compose.yml</h4>
      <pre className="code-block">{`version: '3.8'

services:
  backend:
    build: ./backend
    ports:
      - "4000:4000"
    volumes:
      - ./backend/cranford_publications.db:/app/cranford_publications.db
      - ./backend/uploads:/app/uploads
    environment:
      - NODE_ENV=production
    restart: unless-stopped

  frontend:
    build: ./frontend
    ports:
      - "80:80"
    depends_on:
      - backend
    restart: unless-stopped`}</pre>
    </div>

    <div className="doc-card">
      <h3>Nginx Configuration (Production)</h3>
      <p>For production, use Nginx as reverse proxy:</p>
      <pre className="code-block">{`server {
    listen 80;
    server_name yourdomain.com www.yourdomain.com;
    
    # Redirect HTTP to HTTPS
    return 301 https://$server_name$request_uri;
}

server {
    listen 443 ssl http2;
    server_name yourdomain.com www.yourdomain.com;
    
    ssl_certificate /etc/letsencrypt/live/yourdomain.com/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/yourdomain.com/privkey.pem;
    
    # Frontend (React build)
    location / {
        root /var/www/cranford/frontend/build;
        try_files $uri /index.html;
    }
    
    # Backend API
    location /api {
        proxy_pass http://localhost:4000;
        proxy_http_version 1.1;
        proxy_set_header Upgrade $http_upgrade;
        proxy_set_header Connection 'upgrade';
        proxy_set_header Host $host;
        proxy_cache_bypass $http_upgrade;
    }
    
    # Asset uploads
    location /uploads {
        alias /var/www/cranford/backend/uploads;
    }
}`}</pre>
    </div>

    <div className="doc-card important">
      <h3>🔐 SSL Certificate</h3>
      <p>Use Let's Encrypt for free SSL certificates:</p>
      <div className="code-block">
        sudo certbot --nginx -d yourdomain.com -d www.yourdomain.com
      </div>
    </div>
  </div>
);

const DeploymentSection = () => (
  <div className="doc-section">
    <h2>🚀 Deployment Guide</h2>

    <div className="doc-card important">
      <h3>📋 How Deployment Works</h3>
      <p>Production is deployed to a VPS running <strong>cranfordpub.ca</strong> using Docker + Traefik.
      A versioned shell script (<code>Docker/deploy-X.Y.Z.sh</code>) automates the full pipeline:</p>
      <ol>
        <li><strong>Rsync</strong> — project files are pushed from your laptop to the VPS (excluding <code>node_modules</code>, build artefacts, and the local SQLite DB)</li>
        <li><strong>Remote Docker build</strong> — the image is built <em>on the VPS</em> so you only need Docker on the server, not on your laptop</li>
        <li><strong>Tag latest</strong> — built image is tagged <code>cranfordpubweb:X.Y.Z</code> and <code>cranfordpubweb:latest</code></li>
        <li><strong>Rollout</strong> — <code>docker-compose up -d --force-recreate cranford-app</code> starts the new container</li>
      </ol>
      <p><strong>Requirements on your laptop:</strong> <code>sshpass</code> and <code>rsync</code>.</p>
    </div>

    <div className="doc-card">
      <h3>🖥️ Local Development Setup</h3>

      <h4>1. Install Dependencies</h4>
      <pre className="code-block">{`# Frontend
cd frontend && npm install

# Backend
cd ../backend && npm install`}</pre>

      <h4>2. Configure Environment</h4>
      <pre className="code-block">{`cd backend
cp .env.example .env
# Edit .env — set JWT_SECRET, Stripe/PayPal keys, SMTP credentials`}</pre>

      <h4>3. Start Dev Servers</h4>
      <pre className="code-block">{`# Terminal 1 — Backend (port 4000)
cd backend && npm start

# Terminal 2 — Frontend (port 3000, proxies /api/* to :4000)
cd frontend && npm start`}</pre>
    </div>

    <div className="doc-card">
      <h3>🚢 Production Deploy (One Command)</h3>
      <p>From the <strong>repository root</strong> (not inside <code>Docker/</code>):</p>
      <pre className="code-block">{`# Standard code-only deploy
./Docker/deploy-X.Y.Z.sh

# Also overwrite production SQLite DB with local copy (backs up remote first)
SYNC_DB=1 ./Docker/deploy-X.Y.Z.sh

# Skip rsync (repo already up to date on the VPS — just build + rollout)
SKIP_RSYNC=1 ./Docker/deploy-X.Y.Z.sh

# Build + sync only, no container rollout (restart manually later)
SKIP_ROLLOUT=1 ./Docker/deploy-X.Y.Z.sh`}</pre>

      <p>Replace <code>X.Y.Z</code> with the current release (e.g. <code>deploy-2.13.0.sh</code>).</p>
    </div>

    <div className="doc-card">
      <h3>🔢 Release Version Checklist</h3>
      <p>Before running the deploy script, ensure all version strings are aligned:</p>
      <ul>
        <li>✅ <code>frontend/package.json</code> and <code>backend/package.json</code> → <code>X.Y.Z</code></li>
        <li>✅ Both <code>manifest.json</code> files → <code>"version": "X.Y.Z"</code></li>
        <li>✅ Service worker <code>CACHE_VERSION</code> → <code>cranford-pub-pwa-vX.Y.Z-mp3</code> (forces cache refresh)</li>
        <li>✅ <code>frontend/public/index.html</code> <code>meta name="version"</code> and <code>meta name="updated"</code></li>
        <li>✅ Header version badge in <code>Header.js</code></li>
        <li>✅ <code>Docker/deploy-X.Y.Z.sh</code> — copy previous script, set <code>VERSION</code></li>
        <li>✅ Add <code>NewDocumentation/06-Release-Notes/RELEASE_NOTES_vX.Y.Z.md</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🐳 What the Docker Build Does</h3>
      <pre className="code-block">{`# On the VPS (run automatically by the deploy script):
docker build --no-cache --build-arg VERSION=X.Y.Z \\
  -t cranfordpubweb:X.Y.Z -f Docker/Dockerfile .
docker tag cranfordpubweb:X.Y.Z cranfordpubweb:latest

docker-compose -f Docker/docker-compose-traefik.yml \\
  up -d --force-recreate cranford-app`}</pre>
      <p>The <code>Dockerfile</code> runs <code>npm run build</code> for the React frontend, then packages the static build and backend into a single unified container on port 4000. Traefik handles HTTPS and routes <code>cranfordpub.ca</code> → the container.</p>
    </div>

    <div className="doc-card">
      <h3>✅ Post-Deploy Verification</h3>
      <ol>
        <li>Check API version: <code>GET https://www.cranfordpub.ca/api/version</code> — should match <code>backend/package.json</code></li>
        <li>Hard refresh browser; verify header version badge</li>
        <li>Check container logs on VPS: <code>sudo docker logs cranford-app --tail 200</code></li>
        <li>If DB was synced, spot-check a product or page changed locally</li>
        <li>Smoke test: browse shop, add to cart, check admin dashboard</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>⏪ Rollback</h3>
      <ul>
        <li><strong>Code:</strong> Previous image tags stay on the VPS (<code>docker images cranfordpubweb</code>). Retag the previous version as <code>latest</code> and run force-recreate.</li>
        <li><strong>Database:</strong> Sync script saves a backup at <code>backend/cranford_publications.db.pre_sync_backup.*</code> on the VPS. Restore the file and recreate the container.</li>
      </ul>
      <pre className="code-block">{`# Rollback to previous image (on VPS)
sudo docker tag cranfordpubweb:X.Y.(Z-1) cranfordpubweb:latest
sudo docker-compose -f docker-compose-traefik.yml up -d --force-recreate cranford-app`}</pre>
    </div>

    <div className="doc-card">
      <h3>🗄️ Database Backup (Manual)</h3>
      <pre className="code-block">{`# Local backup snapshot
cp backend/cranford_publications.db \\
   backup/cranford_publications_$(date +%Y%m%d).db

# Sync remote DB down to laptop
./Docker/sync-db-to-vps.sh   # or use scp/rsync manually`}</pre>
    </div>

    <div className="doc-card">
      <h3>🔍 Container Monitoring</h3>
      <pre className="code-block">{`# Live logs
sudo docker logs cranford-app -f --tail=100

# Container status
sudo docker-compose -f Docker/docker-compose-traefik.yml ps

# Restart app only
sudo docker-compose -f Docker/docker-compose-traefik.yml restart cranford-app`}</pre>
    </div>
  </div>
);

const FeaturesSection = () => (
  <div className="doc-section">
    <h2>✨ Features Documentation</h2>
    
    <div className="doc-card">
      <h3>🛒 E-Commerce System</h3>
      <ul>
        <li><strong>Shopping Cart:</strong> Session-based cart with Context API</li>
        <li><strong>Product Catalog:</strong> Filterable by category, searchable</li>
        <li><strong>Product Details:</strong> Dynamic pages with related products</li>
        <li><strong>Checkout:</strong> Multi-step with address validation</li>
        <li><strong>Payment:</strong> Stripe (cards) and PayPal integration</li>
        <li><strong>Order Tracking:</strong> Status updates and email notifications</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>💰 Wholesale System</h3>
      <ul>
        <li><strong>Tiered Pricing:</strong> Standard, Premium, Volume discounts</li>
        <li><strong>Wholesale Roles:</strong> Special pricing visible only to wholesale users</li>
        <li><strong>Bulk Orders:</strong> Quantity-based automatic discounts</li>
        <li><strong>Wholesale Dashboard:</strong> Dedicated view for wholesale customers</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>📝 Content Management</h3>
      <ul>
        <li><strong>Dynamic Pages:</strong> Create pages with custom IDs and types</li>
        <li><strong>Page Types:</strong> general, tune-notation, article, product</li>
        <li><strong>Rich Text Editor:</strong> TipTap for complex formatting</li>
        <li><strong>Simple HTML Editor:</strong> Direct HTML editing with live preview</li>
        <li><strong>Version Control:</strong> Multiple versions per page</li>
        <li><strong>Templates:</strong> Pre-built page templates (tune pages, etc.)</li>
        <li><strong>Metadata:</strong> Type-specific metadata fields (tune info, article dates)</li>
        <li><strong>Bulk Export/Import (ZIP):</strong> Export selected pages with content and associated assets; import pages from ZIP with automatic asset handling</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🎵 Tune Notation System</h3>
      <ul>
        <li><strong>Notation Display:</strong> Image-based music notation</li>
        <li><strong>Audio Player:</strong> Embedded MP3 players</li>
        <li><strong>Metadata:</strong> Artist, tune type, key, time signature</li>
        <li><strong>Related Products:</strong> Automatic linking to books/CDs</li>
        <li><strong>Related Tunes:</strong> Tag-based tune suggestions</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🖼️ Asset Management</h3>
      <ul>
        <li><strong>File Upload:</strong> Images, audio, video, documents</li>
        <li><strong>Organization:</strong> Categories, subcategories, tags</li>
        <li><strong>Search:</strong> Find assets by name, description, tags</li>
        <li><strong>Reusability:</strong> Reference same asset across pages</li>
        <li><strong>Thumbnails:</strong> Automatic thumbnail generation for images</li>
        <li><strong>Access Control:</strong> Public vs private assets</li>
        <li><strong>Duplicate Detection:</strong> Find and manage duplicate files</li>
        <li><strong>Bulk Export/Import (ZIP):</strong> Export selected assets with metadata as ZIP files; import assets from ZIP to quickly migrate or backup content</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>👥 User Management</h3>
      <ul>
        <li><strong>Role-Based Access:</strong> Admin, Content Creator, Wholesale, Retail</li>
        <li><strong>User Profiles:</strong> Editable shipping addresses, preferences</li>
        <li><strong>Email Verification:</strong> Confirm email before full access</li>
        <li><strong>Password Reset:</strong> Secure password recovery flow</li>
        <li><strong>Security Questions:</strong> Additional account recovery method</li>
        <li><strong>Active/Inactive:</strong> Soft delete users</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🎨 Theme System</h3>
      <ul>
        <li><strong>Dark:</strong> High-contrast dark background (default)</li>
        <li><strong>Light:</strong> Bright, clean interface</li>
        <li><strong>Ocean Blue:</strong> Deep blue tones</li>
        <li><strong>Forest Green:</strong> Natural green palette</li>
        <li><strong>Sunset:</strong> Warm orange and amber tones</li>
        <li><strong>Midnight Purple:</strong> Rich purple dark theme</li>
        <li><strong>Persistent:</strong> Choice saved in localStorage across sessions</li>
        <li><strong>CSS Variables:</strong> All colours defined as <code>var(--color-*)</code> — all admin pages are fully theme-aware</li>
        <li><strong>Floating Switcher:</strong> 🎨 palette icon (bottom-right) to change theme at any time</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>📊 Admin Features</h3>
      <ul>
        <li><strong>Order Management:</strong> View, update, track orders</li>
        <li><strong>User Management:</strong> Create, edit, manage users and roles</li>
        <li><strong>Product Management:</strong> CRUD operations with bulk import/export (CSV and ZIP)</li>
        <li><strong>Category Management:</strong> Create categories with custom icons</li>
        <li><strong>Newsletter:</strong> Create and send newsletters to subscribers</li>
        <li><strong>Order Reminders:</strong> Automated reminders for open orders</li>
        <li><strong>Page Visibility:</strong> Control which pages are visible to which roles</li>
        <li><strong>Settings:</strong> Site-wide configuration (inventory display, etc.)</li>
        <li><strong>Bulk Export/Import:</strong> ZIP-based export/import for products, pages, and assets with all associated files and metadata</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🔍 Search & Discovery</h3>
      <ul>
        <li><strong>Product Search:</strong> Search by name, author, artist, tags</li>
        <li><strong>Filters:</strong> Category, price range, availability</li>
        <li><strong>Related Products:</strong> Manual selection + tag-based suggestions</li>
        <li><strong>Asset Search:</strong> Find assets by filename, description, tags</li>
        <li><strong>Universal Search:</strong> Global search across products and pages</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>📦 Bulk Export/Import (ZIP) Feature</h3>
      <p><strong>Location:</strong> Available in Admin &gt; Products, Admin &gt; Pages, and Admin &gt; Assets</p>
      
      <h4>Overview</h4>
      <p>The ZIP export/import feature allows you to bulk transfer products, pages, or assets between environments, create backups, or migrate content. Each ZIP file includes all associated files and metadata.</p>

      <h4>🛍️ Products Export/Import</h4>
      <div className="feature-item" style={{marginTop: '10px'}}>
        <p><strong>Export Single Product:</strong></p>
        <ul>
          <li>Click the export icon (📦) next to any product in the product list</li>
          <li>Downloads a ZIP file containing:
            <ul>
              <li><code>product.json</code> - Product metadata (name, price, description, SKU, etc.)</li>
              <li>Product image file (if present)</li>
              <li><code>README.txt</code> - Import instructions</li>
            </ul>
          </li>
        </ul>

        <p><strong>Export Multiple Products:</strong></p>
        <ul>
          <li>Select multiple products using checkboxes</li>
          <li>Click "Export Selected" button</li>
          <li>Downloads ZIP with all selected products and their images</li>
        </ul>

        <p><strong>Import Product:</strong></p>
        <ul>
          <li>Click "Import Product (ZIP)" button</li>
          <li>Select a ZIP file exported from the system</li>
          <li>Handles SKU conflicts automatically (prompts for create/update/skip)</li>
          <li>Automatically processes and uploads product images</li>
        </ul>
      </div>

      <h4>📄 Pages Export/Import</h4>
      <div className="feature-item" style={{marginTop: '10px'}}>
        <p><strong>Export Pages:</strong></p>
        <ul>
          <li>Select one or more pages using checkboxes</li>
          <li>Click "📦 Export Selected (X)" button</li>
          <li>Downloads a ZIP file containing:
            <ul>
              <li><code>manifest.json</code> - Page metadata, HTML content, and asset references</li>
              <li><code>assets/</code> - Directory with all referenced images, audio files, etc.</li>
              <li><code>README.txt</code> - Import instructions</li>
            </ul>
          </li>
        </ul>

        <p><strong>Import Pages:</strong></p>
        <ul>
          <li>Click "📦 Import ZIP" button</li>
          <li>Select a pages export ZIP file</li>
          <li>Automatically imports pages and creates/updates associated assets</li>
          <li>Preserves page IDs and content versions</li>
          <li>Shows summary: X pages imported/updated, Y assets created/updated</li>
        </ul>
      </div>

      <h4>🖼️ Assets Export/Import</h4>
      <div className="feature-item" style={{marginTop: '10px'}}>
        <p><strong>Export Assets:</strong></p>
        <ul>
          <li>Select one or more assets using checkboxes</li>
          <li>Click "📦 Export Selected (X)" button</li>
          <li>Downloads a ZIP file containing:
            <ul>
              <li><code>manifest.json</code> - Asset metadata (filenames, descriptions, tags, categories)</li>
              <li>All selected asset files in their original format</li>
            </ul>
          </li>
        </ul>

        <p><strong>Import Assets:</strong></p>
        <ul>
          <li>Click "📦 Import ZIP" button</li>
          <li>Select an assets export ZIP file</li>
          <li>Automatically imports assets with metadata</li>
          <li>Updates existing assets or creates new ones</li>
          <li>Shows summary: X imported, Y updated</li>
        </ul>
      </div>

      <h4>💡 Best Practices</h4>
      <ul>
        <li><strong>Backup Before Import:</strong> Always export current content before bulk imports</li>
        <li><strong>Test Imports:</strong> Test ZIP imports in development before production</li>
        <li><strong>Review Conflicts:</strong> Pay attention to SKU/ID conflict resolution prompts</li>
        <li><strong>Maintain Consistency:</strong> Keep export ZIPs organized with clear naming (e.g., <code>products_export_20251102.zip</code>)</li>
        <li><strong>Version Control:</strong> Archive export ZIPs for rollback capability</li>
      </ul>

      <h4>⚠️ Important Notes</h4>
      <ul>
        <li><strong>File Size Limits:</strong> ZIP imports are limited to 100MB by default</li>
        <li><strong>Asset Handling:</strong> Asset files are automatically created if they don't exist</li>
        <li><strong>Page IDs:</strong> Page imports preserve original page IDs; conflicts will update existing pages</li>
        <li><strong>Product SKUs:</strong> Product imports detect SKU conflicts and prompt for resolution</li>
        <li><strong>Cross-Platform:</strong> ZIP files work across different environments (dev, staging, production)</li>
      </ul>
    </div>
  </div>
);

const SecuritySection = () => (
  <div className="doc-section">
    <h2>🔒 Security</h2>
    
    <div className="doc-card important">
      <h3>🔐 Authentication & Authorization</h3>
      
      <h4>Password Security</h4>
      <ul>
        <li><strong>Hashing:</strong> bcrypt with salt rounds (cost factor: 10)</li>
        <li><strong>Password Requirements:</strong> Minimum 8 characters (configurable)</li>
        <li><strong>Storage:</strong> Never store plain text passwords</li>
      </ul>

      <h4>JWT Tokens</h4>
      <ul>
        <li><strong>Algorithm:</strong> HS256 (HMAC with SHA-256)</li>
        <li><strong>Expiration:</strong> 7 days (configurable via JWT_EXPIRES_IN)</li>
        <li><strong>Storage:</strong> localStorage on client, httpOnly recommended for production</li>
        <li><strong>Payload:</strong> {`{ userId, username, roles }`}</li>
      </ul>

      <h4>Role-Based Access Control (RBAC)</h4>
      <ul>
        <li><strong>admin:</strong> Full access to everything</li>
        <li><strong>super_admin:</strong> Extended admin privileges</li>
        <li><strong>content_creator:</strong> Create/edit pages and assets</li>
        <li><strong>wholesale:</strong> See wholesale pricing, place bulk orders</li>
        <li><strong>customer:</strong> Basic shopping and profile access</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🛡️ API Security</h3>
      
      <h4>Authentication Middleware</h4>
      <pre className="code-block">{`// backend/middleware/auth.js
function authenticate(req, res, next) {
  const token = req.headers.authorization?.split(' ')[1];
  if (!token) return res.status(401).json({ error: 'Unauthorized' });
  
  try {
    const decoded = jwt.verify(token, process.env.JWT_SECRET);
    req.user = decoded;
    next();
  } catch (error) {
    res.status(401).json({ error: 'Invalid token' });
  }
}`}</pre>

      <h4>Authorization Checks</h4>
      <pre className="code-block">{`function requireAdmin(req, res, next) {
  if (!req.user?.roles?.includes('admin')) {
    return res.status(403).json({ error: 'Forbidden' });
  }
  next();
}`}</pre>
    </div>

    <div className="doc-card">
      <h3>🔒 Data Protection</h3>
      <ul>
        <li><strong>HTTPS:</strong> All production traffic over SSL/TLS</li>
        <li><strong>CORS:</strong> Restricted to allowed origins</li>
        <li><strong>SQL Injection:</strong> Prevented via parameterized queries</li>
        <li><strong>XSS:</strong> React auto-escapes content, dangerouslySetInnerHTML used carefully</li>
        <li><strong>CSRF:</strong> Token-based authentication reduces risk</li>
        <li><strong>File Uploads:</strong> Type validation, size limits, stored outside web root</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>💳 Payment Security</h3>
      <ul>
        <li><strong>PCI Compliance:</strong> Stripe handles all card data (PCI DSS compliant)</li>
        <li><strong>No Card Storage:</strong> Never store card numbers on server</li>
        <li><strong>Tokenization:</strong> Stripe creates payment tokens</li>
        <li><strong>Server-Side Processing:</strong> All payment logic on backend</li>
        <li><strong>Webhook Verification:</strong> Stripe webhooks verified with signatures</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>⚠️ Security Best Practices</h3>
      <ol>
        <li><strong>Change Default Secrets:</strong> Never use default JWT_SECRET in production</li>
        <li><strong>Environment Variables:</strong> Never commit .env files to git</li>
        <li><strong>Regular Updates:</strong> Keep dependencies updated (npm audit)</li>
        <li><strong>Rate Limiting:</strong> Implement rate limiting for API endpoints</li>
        <li><strong>Input Validation:</strong> Validate all user inputs on server side</li>
        <li><strong>Error Handling:</strong> Don't expose sensitive info in error messages</li>
        <li><strong>Database Backups:</strong> Regular automated backups</li>
        <li><strong>Monitoring:</strong> Log suspicious activities</li>
        <li><strong>Admin Access:</strong> Use strong passwords, enable 2FA if possible</li>
        <li><strong>File Permissions:</strong> Restrict access to database and upload directories</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>🔍 Security Checklist</h3>
      <ul>
        <li>✅ Strong JWT_SECRET (min 32 characters, random)</li>
        <li>✅ HTTPS enabled with valid SSL certificate</li>
        <li>✅ Environment variables properly set</li>
        <li>✅ Stripe/PayPal keys are production keys</li>
        <li>✅ Database file permissions restricted</li>
        <li>✅ Upload directory outside public web root</li>
        <li>✅ CORS configured correctly</li>
        <li>✅ Error messages don't leak sensitive data</li>
        <li>✅ Rate limiting implemented</li>
        <li>✅ Regular backups automated</li>
        <li>✅ Dependencies scanned for vulnerabilities</li>
      </ul>
    </div>
  </div>
);

const TroubleshootingSection = () => (
  <div className="doc-section">
    <h2>🔧 Troubleshooting</h2>
    
    <div className="doc-card">
      <h3>Common Issues</h3>
      
      <h4>❌ "Cannot connect to backend"</h4>
      <div className="troubleshoot-item">
        <strong>Symptoms:</strong> Frontend shows "Network Error" or API calls fail
        <br/>
        <strong>Solutions:</strong>
        <ul>
          <li>Check backend is running: <code>curl http://localhost:4000/api/products</code></li>
          <li>Verify proxy configuration in frontend/package.json: <code>"proxy": "http://localhost:4000"</code></li>
          <li>Check backend logs: <code>pm2 logs cranford-backend</code></li>
          <li>Ensure firewall allows port 4000</li>
        </ul>
      </div>

      <h4>❌ "Database locked" error</h4>
      <div className="troubleshoot-item">
        <strong>Symptoms:</strong> SQLite database locked errors
        <br/>
        <strong>Solutions:</strong>
        <ul>
          <li>Close any open database connections</li>
          <li>Check for long-running queries</li>
          <li>Restart backend server</li>
          <li>If persistent, check database file permissions</li>
        </ul>
      </div>

      <h4>❌ "JWT token invalid"</h4>
      <div className="troubleshoot-item">
        <strong>Symptoms:</strong> Users logged out unexpectedly, 401 errors
        <br/>
        <strong>Solutions:</strong>
        <ul>
          <li>Check JWT_SECRET matches between environments</li>
          <li>Verify token expiration time</li>
          <li>Clear localStorage and re-login</li>
          <li>Check for clock skew on server</li>
        </ul>
      </div>

      <h4>❌ File upload fails</h4>
      <div className="troubleshoot-item">
        <strong>Symptoms:</strong> Assets not uploading, 413 or 500 errors
        <br/>
        <strong>Solutions:</strong>
        <ul>
          <li>Check file size against MAX_FILE_SIZE limit</li>
          <li>Verify upload directory permissions: <code>chmod 755 backend/uploads</code></li>
          <li>Check disk space: <code>df -h</code></li>
          <li>Verify Multer configuration in backend</li>
        </ul>
      </div>

      <h4>❌ Images not displaying</h4>
      <div className="troubleshoot-item">
        <strong>Symptoms:</strong> Broken image icons, 404 for image requests
        <br/>
        <strong>Solutions:</strong>
        <ul>
          <li>Check image path in database matches actual file location</li>
          <li>Verify Nginx/Apache serving uploads directory correctly</li>
          <li>Check file permissions: <code>ls -la backend/uploads/</code></li>
          <li>Verify asset ID is correct in frontend</li>
        </ul>
      </div>
    </div>

    <div className="doc-card">
      <h3>Debugging Tools</h3>
      
      <h4>Backend Logs</h4>
      <pre className="code-block">{`# PM2 logs
pm2 logs cranford-backend

# Tail logs
pm2 logs cranford-backend --lines 100

# Error logs only
pm2 logs cranford-backend --err`}</pre>

      <h4>Database Queries</h4>
      <pre className="code-block">{`# Open SQLite database
sqlite3 backend/cranford_publications.db

# List tables
.tables

# Check user count
SELECT COUNT(*) FROM users;

# Find admin users
SELECT id, username, email, roles FROM users WHERE roles LIKE '%admin%';

# Check recent orders
SELECT * FROM orders ORDER BY created_at DESC LIMIT 10;`}</pre>

      <h4>React DevTools</h4>
      <ul>
        <li>Install React DevTools browser extension</li>
        <li>Inspect component props and state</li>
        <li>Check Context values (Auth, Cart, Theme)</li>
        <li>Profile component rendering performance</li>
      </ul>

      <h4>Network Debugging</h4>
      <ul>
        <li>Browser DevTools → Network tab</li>
        <li>Check API response status codes</li>
        <li>Verify request headers (Authorization token)</li>
        <li>Inspect response bodies for errors</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Performance Issues</h3>
      
      <h4>Slow Page Load</h4>
      <ul>
        <li>Run production build: <code>npm run build</code></li>
        <li>Enable compression middleware in backend</li>
        <li>Optimize images (reduce file size)</li>
        <li>Implement lazy loading for images</li>
        <li>Check database indexes</li>
      </ul>

      <h4>High Memory Usage</h4>
      <ul>
        <li>Check PM2 memory: <code>pm2 monit</code></li>
        <li>Restart backend if memory leak: <code>pm2 restart cranford-backend</code></li>
        <li>Review recent code changes for memory leaks</li>
        <li>Limit query result sizes</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Recovery Procedures</h3>
      
      <h4>Restore Database Backup</h4>
      <pre className="code-block">{`# Stop backend
pm2 stop cranford-backend

# Restore backup
cp backup/cranford_publications_YYYYMMDD.db backend/cranford_publications.db

# Start backend
pm2 start cranford-backend`}</pre>

      <h4>Reset Admin Password</h4>
      <pre className="code-block">{`# Open database
sqlite3 backend/cranford_publications.db

# Generate new bcrypt hash (use bcrypt tool or Node.js)
# Then update:
UPDATE users SET password='$2b$10$newHashHere' WHERE username='admin';`}</pre>

      <h4>Clear All Sessions</h4>
      <p>Have users clear localStorage and re-login, or invalidate tokens by changing JWT_SECRET (forces all re-logins)</p>
    </div>

    <div className="doc-card important">
      <h3>🆘 Emergency Contacts</h3>
      <ul>
        <li><strong>System Admin:</strong> Your contact info here</li>
        <li><strong>Hosting Support:</strong> Your hosting provider</li>
        <li><strong>Stripe Support:</strong> https://support.stripe.com</li>
        <li><strong>PayPal Support:</strong> https://www.paypal.com/help</li>
      </ul>
    </div>
  </div>
);

const DockerSection = () => (
  <div className="doc-section">
    <h2>🐳 Docker Infrastructure</h2>
    
    <div className="doc-card">
      <h3>Infrastructure Overview</h3>
      <p>The Cranford Publications infrastructure consists of two deployment architectures:</p>
      
      <h4>Development Application (Current Focus)</h4>
      <p>Single unified container for the Cranford Publications web application</p>
      
      <h4>Production Infrastructure Stack (Future/Separate)</h4>
      <p>Complete production infrastructure with 8 services on VPS (YOUR_VPS_IP)</p>
    </div>

    <div className="doc-card">
      <h3>Application Architecture Options</h3>
      <p>The web application supports two container architectures:</p>
      
      <div className="feature-grid">
        <div className="feature-item">
          <h4>🎯 Unified Container (Recommended)</h4>
          <p>Frontend and backend in a single container on port 4000</p>
          <ul>
            <li>Simpler deployment</li>
            <li>No CORS issues</li>
            <li>Faster startup</li>
            <li>Smaller footprint</li>
          </ul>
        </div>
        <div className="feature-item">
          <h4>📦 Separate Containers (Legacy)</h4>
          <p>Frontend (port 3000) and backend (port 4000) separate</p>
          <ul>
            <li>Independent scaling</li>
            <li>Separate updates</li>
            <li>Development flexibility</li>
            <li>Requires proxy config</li>
          </ul>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>Production Infrastructure Stack</h3>
      <p><strong>Server:</strong> YOUR_VPS_IP (cranfordpub.ca)</p>
      
      <h4>Services Overview</h4>
      <div className="api-endpoint-group">
        <table style={{width: '100%', borderCollapse: 'collapse'}}>
          <thead>
            <tr style={{borderBottom: '2px solid var(--color-border)'}}>
              <th style={{textAlign: 'left', padding: '8px'}}>Service</th>
              <th style={{textAlign: 'left', padding: '8px'}}>Container</th>
              <th style={{textAlign: 'left', padding: '8px'}}>Purpose</th>
              <th style={{textAlign: 'left', padding: '8px'}}>URL</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={{padding: '8px'}}>Traefik</td><td style={{padding: '8px'}}>traefik</td><td style={{padding: '8px'}}>Reverse proxy & SSL</td><td style={{padding: '8px'}}>traefik.cranfordpub.ca</td></tr>
            <tr><td style={{padding: '8px'}}>DMS</td><td style={{padding: '8px'}}>dms</td><td style={{padding: '8px'}}>Mail server</td><td style={{padding: '8px'}}>mail.cranfordpub.ca</td></tr>
            <tr><td style={{padding: '8px'}}>PostgreSQL</td><td style={{padding: '8px'}}>postgres</td><td style={{padding: '8px'}}>Database server</td><td style={{padding: '8px'}}>postgres:5432</td></tr>
            <tr><td style={{padding: '8px'}}>pgAdmin</td><td style={{padding: '8px'}}>pgadmin</td><td style={{padding: '8px'}}>Database UI</td><td style={{padding: '8px'}}>pgadmin.cranfordpub.ca</td></tr>
            <tr><td style={{padding: '8px'}}>Node-RED</td><td style={{padding: '8px'}}>nodered</td><td style={{padding: '8px'}}>Automation</td><td style={{padding: '8px'}}>nodered.cranfordpub.ca</td></tr>
            <tr><td style={{padding: '8px'}}>Portainer</td><td style={{padding: '8px'}}>portainer</td><td style={{padding: '8px'}}>Docker UI</td><td style={{padding: '8px'}}>portainer.cranfordpub.ca</td></tr>
            <tr><td style={{padding: '8px'}}>Rainloop</td><td style={{padding: '8px'}}>rainloop</td><td style={{padding: '8px'}}>Webmail</td><td style={{padding: '8px'}}>webmail.cranfordpub.ca</td></tr>
            <tr><td style={{padding: '8px'}}>Frontend</td><td style={{padding: '8px'}}>frontend</td><td style={{padding: '8px'}}>React app</td><td style={{padding: '8px'}}>cranfordpub.ca</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <div className="doc-card">
      <h3>Network Architecture</h3>
      <pre className="architecture-diagram">{`┌─────────────────────────────────────────────────────────┐
│              Internet (Port 80/443)                     │
└───────────────────────┬─────────────────────────────────┘
                        │
                ┌───────▼────────┐
                │    Traefik     │ (Reverse Proxy + SSL)
                │  Port 80, 443  │
                └───────┬────────┘
                        │
        ┌───────────────┼───────────────┐
        │               │               │
┌───────▼──────┐ ┌─────▼─────┐ ┌──────▼──────┐
│   Frontend   │ │  pgAdmin  │ │  Node-RED   │
│   Port 3000  │ │ Port 5050 │ │  Port 1880  │
└──────────────┘ └───────────┘ └─────────────┘
        │
┌───────▼──────────────────────────────────────┐
│         traefik-public network               │
│  (External access via Traefik)               │
└──────────────────────────────────────────────┘
        │
┌───────▼──────────────────────────────────────┐
│         internal network                      │
│  (Backend services communication)            │
└───────┬──────────────┬───────────────────────┘
        │              │
┌───────▼──────┐ ┌────▼─────┐
│  PostgreSQL  │ │   DMS    │
│  Port 5432   │ │Mail Ports│
└──────────────┘ └──────────┘

Mail Ports (Direct Exposure):
- Port 25 (SMTP)
- Port 143 (IMAP)
- Port 465 (SMTPS)
- Port 587 (Submission)
- Port 993 (IMAPS)`}</pre>

      <h4>Docker Networks</h4>
      <ul>
        <li><strong>traefik-public (External):</strong> Routes external traffic through Traefik
          <ul>
            <li>Services: traefik, frontend, pgadmin, nodered, portainer, rainloop, dms</li>
            <li>Driver: bridge</li>
          </ul>
        </li>
        <li><strong>internal (Internal):</strong> Backend service communication
          <ul>
            <li>Services: postgres, dms, rainloop</li>
            <li>Driver: bridge</li>
            <li>No external access</li>
          </ul>
        </li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Volume Mappings</h3>
      <table style={{width: '100%', fontSize: '0.9em'}}>
        <thead>
          <tr style={{borderBottom: '2px solid var(--color-border)'}}>
            <th style={{textAlign: 'left', padding: '8px'}}>Volume/Bind Mount</th>
            <th style={{textAlign: 'left', padding: '8px'}}>Container Path</th>
            <th style={{textAlign: 'left', padding: '8px'}}>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr><td style={{padding: '8px'}}><code>./certs</code></td><td style={{padding: '8px'}}>/certs</td><td style={{padding: '8px'}}>Traefik SSL certificates</td></tr>
          <tr><td style={{padding: '8px'}}><code>postgres-data</code></td><td style={{padding: '8px'}}>/var/lib/postgresql/data</td><td style={{padding: '8px'}}>PostgreSQL database</td></tr>
          <tr><td style={{padding: '8px'}}><code>pgadmin-data</code></td><td style={{padding: '8px'}}>/var/lib/pgadmin</td><td style={{padding: '8px'}}>pgAdmin config</td></tr>
          <tr><td style={{padding: '8px'}}><code>node-red-data</code></td><td style={{padding: '8px'}}>/data</td><td style={{padding: '8px'}}>Node-RED flows</td></tr>
          <tr><td style={{padding: '8px'}}><code>portainer-data</code></td><td style={{padding: '8px'}}>/data</td><td style={{padding: '8px'}}>Portainer config</td></tr>
          <tr><td style={{padding: '8px'}}><code>./dms-data</code></td><td style={{padding: '8px'}}>/var/mail</td><td style={{padding: '8px'}}>DMS mail storage</td></tr>
          <tr><td style={{padding: '8px'}}><code>./dms-config</code></td><td style={{padding: '8px'}}>/tmp/docker-mailserver</td><td style={{padding: '8px'}}>DMS configuration</td></tr>
          <tr><td style={{padding: '8px'}}><code>./rainloop-data</code></td><td style={{padding: '8px'}}>/rainloop/data</td><td style={{padding: '8px'}}>Rainloop config</td></tr>
        </tbody>
      </table>
    </div>

    <div className="doc-card">
      <h3>Application Commands</h3>
      
      <h4>Development Application</h4>
      <pre className="code-block">{`# Start unified container (production)
./Docker/podman-unified-start.sh

# Start development mode with hot reload
./Docker/podman-unified-start.sh dev

# Stop containers
./Docker/podman-unified-stop.sh

# View logs
docker logs -f cranford-app`}</pre>

      <h4>Production Infrastructure</h4>
      <pre className="code-block">{`# SSH to server
ssh YOUR_SSH_USER@YOUR_VPS_IP

# Navigate to project
cd /home/YOUR_SSH_USER/cranfordpub

# View all services
docker-compose ps

# View logs
docker-compose logs -f [service]

# Restart service
docker-compose restart [service]

# Update all services
docker-compose pull
docker-compose up -d --force-recreate`}</pre>
    </div>

    <div className="doc-card">
      <h3>Maintenance Tasks</h3>
      
      <h4>Daily</h4>
      <ul>
        <li>Check service status: <code>docker-compose ps</code></li>
        <li>Review logs: <code>docker-compose logs --tail=50 -f</code></li>
      </ul>

      <h4>Weekly</h4>
      <ul>
        <li>Check certificate expiry</li>
        <li>Review disk usage: <code>docker system df</code></li>
        <li>Check mail queue: <code>docker exec dms postqueue -p</code></li>
      </ul>

      <h4>Monthly</h4>
      <ul>
        <li>Update Docker images: <code>docker-compose pull && docker-compose up -d</code></li>
        <li>Clean up Docker: <code>docker system prune -a</code></li>
        <li>Backup database and mail data</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>🎯 Best Practices</h3>
      <ul>
        <li><strong>Use Unified Container:</strong> Simpler deployment, fewer issues</li>
        <li><strong>Volume Management:</strong> Always use named volumes for data persistence</li>
        <li><strong>Health Checks:</strong> Ensure containers are healthy before routing traffic</li>
        <li><strong>Resource Limits:</strong> Set CPU and memory limits for predictable performance</li>
        <li><strong>Environment Variables:</strong> Never hardcode secrets in Dockerfiles</li>
        <li><strong>Regular Backups:</strong> Backup volumes, databases, and configuration files</li>
        <li><strong>Monitor Logs:</strong> Check logs regularly for errors and warnings</li>
        <li><strong>Update Images:</strong> Keep Docker images up to date for security</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Security Considerations</h3>
      
      <h4>Firewall Configuration (Production Server)</h4>
      <p><strong>Required Open Ports:</strong></p>
      <ul>
        <li>80 (HTTP - for Let's Encrypt challenges)</li>
        <li>443 (HTTPS - web services)</li>
        <li>25 (SMTP - mail transfer)</li>
        <li>143 (IMAP - mail retrieval)</li>
        <li>465 (SMTPS - secure submission)</li>
        <li>587 (Submission - STARTTLS)</li>
        <li>993 (IMAPS - secure IMAP)</li>
      </ul>

      <h4>Access Control</h4>
      <ul>
        <li><strong>SSH Access:</strong> Key-based authentication recommended</li>
        <li><strong>Docker Socket:</strong> Limited to YOUR_SSH_USER user</li>
        <li><strong>Service Authentication:</strong> All services require authentication</li>
        <li><strong>Network Isolation:</strong> Internal network for backend services</li>
      </ul>
    </div>
  </div>
);

const PostgresMigrationSection = () => (
  <div className="doc-section">
    <h2>🔄 PostgreSQL Migration Plan</h2>
    
    <div className="doc-card important">
      <h3>⚠️ Current Status: Planning Phase</h3>
      <p>The application currently uses <strong>SQLite</strong> for data storage. Migration to <strong>PostgreSQL</strong> is planned for future scalability.</p>
      
      <div className="feature-grid">
        <div className="feature-item">
          <h4>✅ Current: SQLite</h4>
          <ul>
            <li>File-based database</li>
            <li>Simple deployment</li>
            <li>Single-user optimized</li>
            <li>Portable</li>
          </ul>
        </div>
        <div className="feature-item">
          <h4>🚀 Future: PostgreSQL</h4>
          <ul>
            <li>Client-server architecture</li>
            <li>Multi-user concurrent access</li>
            <li>Better scalability</li>
            <li>Advanced features</li>
          </ul>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>Why Migrate to PostgreSQL?</h3>
      
      <h4>Performance Benefits</h4>
      <ul>
        <li><strong>Concurrent Connections:</strong> Handle multiple simultaneous users efficiently</li>
        <li><strong>Complex Queries:</strong> Better performance for JOIN operations</li>
        <li><strong>Indexing:</strong> Advanced indexing strategies</li>
      </ul>

      <h4>Feature Enhancements</h4>
      <ul>
        <li><strong>Full-Text Search:</strong> Built-in FTS capabilities</li>
        <li><strong>JSON Support:</strong> Native JSON/JSONB data types</li>
        <li><strong>Stored Procedures:</strong> Complex business logic in database</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Expected Timeline</h3>
      <div className="feature-grid">
        <div className="feature-item">
          <h4>Phase 1-2: Preparation</h4>
          <p>1-2 weeks</p>
          <small>Schema design and testing</small>
        </div>
        <div className="feature-item">
          <h4>Phase 3-4: Migration</h4>
          <p>2-3 weeks</p>
          <small>Data transfer and code updates</small>
        </div>
        <div className="feature-item">
          <h4>Phase 5: Testing</h4>
          <p>1-2 weeks</p>
          <small>Comprehensive validation</small>
        </div>
        <div className="feature-item">
          <h4>Phase 6: Deployment</h4>
          <p>1 day</p>
          <small>Production migration</small>
        </div>
      </div>
      <p><strong>Total Estimated Time:</strong> 5-8 weeks</p>
    </div>
  </div>
);

const MailServerSection = () => (
  <div className="doc-section">
    <h2>📧 Mail Server (DMS) & Webmail</h2>
    
    <div className="doc-card">
      <h3>Docker Mail Server (DMS)</h3>
      <p>The infrastructure uses <strong>Docker Mail Server (DMS)</strong>, a production-ready mail server solution running on the production VPS (YOUR_VPS_IP).</p>
      
      <h4>Server Configuration</h4>
      <ul>
        <li><strong>Hostname:</strong> mail.cranfordpub.ca</li>
        <li><strong>Domain:</strong> cranfordpub.ca</li>
        <li><strong>Container:</strong> dms</li>
        <li><strong>Timezone:</strong> America/Toronto</li>
      </ul>

      <h4>Supported Protocols & Ports</h4>
      <table style={{width: '100%', fontSize: '0.9em'}}>
        <thead>
          <tr style={{borderBottom: '2px solid var(--color-border)'}}>
            <th style={{textAlign: 'left', padding: '8px'}}>Port</th>
            <th style={{textAlign: 'left', padding: '8px'}}>Protocol</th>
            <th style={{textAlign: 'left', padding: '8px'}}>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr><td style={{padding: '8px'}}>25</td><td style={{padding: '8px'}}>SMTP</td><td style={{padding: '8px'}}>Mail Transfer</td></tr>
          <tr><td style={{padding: '8px'}}>143</td><td style={{padding: '8px'}}>IMAP</td><td style={{padding: '8px'}}>Mail Retrieval</td></tr>
          <tr><td style={{padding: '8px'}}>465</td><td style={{padding: '8px'}}>SMTPS</td><td style={{padding: '8px'}}>SSL/TLS Submission</td></tr>
          <tr><td style={{padding: '8px'}}>587</td><td style={{padding: '8px'}}>Submission</td><td style={{padding: '8px'}}>STARTTLS Submission</td></tr>
          <tr><td style={{padding: '8px'}}>993</td><td style={{padding: '8px'}}>IMAPS</td><td style={{padding: '8px'}}>SSL/TLS IMAP</td></tr>
        </tbody>
      </table>
    </div>

    <div className="doc-card">
      <h3>Security Features</h3>
      <p>DMS includes comprehensive security and anti-spam protection:</p>
      
      <div className="feature-grid">
        <div className="feature-item">
          <h4>🛡️ Anti-Spam</h4>
          <ul>
            <li><strong>SpamAssassin:</strong> Content filtering</li>
            <li><strong>Postgrey:</strong> Greylisting</li>
            <li><strong>Fail2Ban:</strong> Intrusion prevention</li>
          </ul>
        </div>
        <div className="feature-item">
          <h4>🔐 Authentication</h4>
          <ul>
            <li><strong>OpenDKIM:</strong> DKIM signing</li>
            <li><strong>OpenDMARC:</strong> DMARC validation</li>
            <li><strong>SPF:</strong> Sender Policy Framework</li>
          </ul>
        </div>
        <div className="feature-item">
          <h4>🦠 Anti-Virus</h4>
          <ul>
            <li><strong>ClamAV:</strong> Virus scanning</li>
            <li><strong>Amavis:</strong> Content filtering</li>
          </ul>
        </div>
        <div className="feature-item">
          <h4>💾 Quotas</h4>
          <ul>
            <li><strong>Storage:</strong> 1GB per user</li>
            <li><strong>Messages:</strong> 1000 max count</li>
            <li><strong>Size Limit:</strong> 50MB per message</li>
          </ul>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>🔐 SSL/TLS Certificate Management</h3>
      <p>DMS uses <strong>manual SSL configuration</strong> with certificates extracted from Traefik's Let's Encrypt store.</p>
      
      <h4>Certificate Flow</h4>
      <pre className="architecture-diagram">{`Let's Encrypt
     │
     ▼
Traefik (acme.json)
     │
     ▼
extract-traefik-certs.sh (Cronjob - Daily 3:00 AM)
     │
     ▼
/home/YOUR_SSH_USER/cranfordpub/dms-config/ssl/
     │
     ├─ cert.pem (Certificate)
     └─ key.pem (Private Key)
     │
     ▼
DMS Container (/tmp/docker-mailserver/ssl/)
     │
     ▼
Postfix & Dovecot (Mail Services)`}</pre>

      <h4>Automated Certificate Extraction</h4>
      <p><strong>Script:</strong> <code>/home/YOUR_SSH_USER/cranfordpub/extract-traefik-certs.sh</code></p>
      <p><strong>Schedule:</strong> Daily at 3:00 AM via cronjob</p>
      <p><strong>Purpose:</strong> Extracts SSL certificates from Traefik's acme.json and places them in DMS configuration</p>

      <h4>Manual Certificate Operations</h4>
      <pre className="code-block">{`# Extract certificates manually
/home/YOUR_SSH_USER/cranfordpub/extract-traefik-certs.sh

# Reload Postfix to apply new certificates
docker exec dms postfix reload

# Check certificate expiry
openssl x509 -in /home/YOUR_SSH_USER/cranfordpub/dms-config/ssl/cert.pem -noout -dates

# Test SMTP TLS
openssl s_client -connect mail.cranfordpub.ca:587 -starttls smtp -quiet

# Test IMAP TLS
openssl s_client -connect mail.cranfordpub.ca:143 -starttls imap -quiet

# Verify Postfix TLS configuration
docker exec dms postconf | grep -E "smtpd_tls_cert_file|smtpd_tls_key_file"`}</pre>
    </div>

    <div className="doc-card">
      <h3>Rainloop Webmail</h3>
      <p><strong>Rainloop</strong> provides modern browser-based email access.</p>
      
      <h4>Access Information</h4>
      <ul>
        <li><strong>URL:</strong> <a href="https://webmail.cranfordpub.ca" target="_blank" rel="noopener noreferrer">https://webmail.cranfordpub.ca</a></li>
        <li><strong>Login:</strong> Full email address (e.g., user@cranfordpub.ca)</li>
        <li><strong>Admin Panel:</strong> Add <code>?admin</code> to URL</li>
        <li><strong>Default Admin:</strong> admin / 12345 (⚠️ change immediately!)</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Email Client Configuration</h3>
      <p>Configure desktop email clients (Thunderbird, Outlook, Apple Mail, etc.) with these settings:</p>
      
      <div className="feature-grid">
        <div className="feature-item">
          <h4>📨 IMAP (Receiving)</h4>
          <pre className="code-block">{`Server: mail.cranfordpub.ca
Port: 993
Security: SSL/TLS
Username: full@email.address
Authentication: Normal password`}</pre>
        </div>
        <div className="feature-item">
          <h4>📤 SMTP (Sending)</h4>
          <pre className="code-block">{`Server: mail.cranfordpub.ca
Port: 587
Security: STARTTLS
Username: full@email.address
Authentication: Normal password`}</pre>
        </div>
      </div>

      <h4>Alternative Ports</h4>
      <ul>
        <li><strong>IMAP:</strong> Port 143 (STARTTLS) or 993 (SSL/TLS) - recommended 993</li>
        <li><strong>SMTP:</strong> Port 587 (STARTTLS) or 465 (SSL/TLS) - recommended 587</li>
        <li><strong>POP3:</strong> Available if needed (not recommended, use IMAP)</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Mail Management Commands</h3>
      
      <h4>User Management</h4>
      <pre className="code-block">{`# Add email account
docker exec dms setup email add user@cranfordpub.ca password

# List all email accounts
docker exec dms setup email list

# Delete email account
docker exec dms setup email del user@cranfordpub.ca

# Update password
docker exec dms setup email update user@cranfordpub.ca newpassword`}</pre>

      <h4>Mail Queue & Logs</h4>
      <pre className="code-block">{`# Check mail queue
docker exec dms postqueue -p

# View mail logs
docker logs dms --tail=100 -f

# View specific log files
docker exec dms tail -f /var/log/mail/mail.log

# Flush mail queue (retry sending)
docker exec dms postqueue -f`}</pre>
    </div>

    <div className="doc-card">
      <h3>🐛 Troubleshooting</h3>
      
      <h4>SSL/TLS Issues</h4>
      <pre className="code-block">{`# Verify certificates exist
ls -lh /home/YOUR_SSH_USER/cranfordpub/dms-config/ssl/

# Re-extract certificates
/home/YOUR_SSH_USER/cranfordpub/extract-traefik-certs.sh

# Check Postfix TLS config
docker exec dms postconf | grep tls

# Reload Postfix
docker exec dms postfix reload`}</pre>

      <h4>Mail Not Sending/Receiving</h4>
      <pre className="code-block">{`# Check mail queue
docker exec dms postqueue -p

# Check logs for errors
docker logs dms --tail=100 | grep -i error

# Test SMTP locally
docker exec dms nc localhost 25

# Test SMTP externally
nc -zv mail.cranfordpub.ca 587

# Restart DMS container
docker-compose restart dms`}</pre>

      <h4>Container Issues</h4>
      <pre className="code-block">{`# Check container status
docker-compose ps dms

# View detailed logs
docker logs dms --tail=200

# Recreate container
docker-compose up -d --force-recreate dms

# Check configuration
docker-compose config`}</pre>
    </div>

    <div className="doc-card important">
      <h3>⚠️ Mail Server Best Practices</h3>
      <ul>
        <li><strong>SPF Records:</strong> Configure DNS TXT record for sender validation</li>
        <li><strong>DKIM Signing:</strong> Ensure OpenDKIM is properly configured</li>
        <li><strong>DMARC Policy:</strong> Set up DMARC DNS record for email authentication</li>
        <li><strong>Reverse DNS (PTR):</strong> Ensure PTR record points to mail.cranfordpub.ca</li>
        <li><strong>SSL Certificates:</strong> Monitored daily via cronjob, auto-renewed by Traefik</li>
        <li><strong>Strong Passwords:</strong> Enforce complex passwords for all email accounts</li>
        <li><strong>Monitor Blacklists:</strong> Check regularly at mxtoolbox.com</li>
        <li><strong>Regular Backups:</strong> Backup /home/YOUR_SSH_USER/cranfordpub/dms-data/</li>
        <li><strong>Log Monitoring:</strong> Review mail logs weekly for suspicious activity</li>
        <li><strong>Spam Filters:</strong> Keep SpamAssassin rules updated</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>📦 Backup & Restore</h3>
      
      <h4>Backup Mail Data</h4>
      <pre className="code-block">{`# Backup all mail data
tar -czf dms-backup_$(date +%Y%m%d).tar.gz /home/YOUR_SSH_USER/cranfordpub/dms-data

# Backup configuration
tar -czf dms-config_$(date +%Y%m%d).tar.gz /home/YOUR_SSH_USER/cranfordpub/dms-config

# Backup user accounts
docker exec dms cat /tmp/docker-mailserver/postfix-accounts.cf > accounts_backup.txt`}</pre>

      <h4>Restore Mail Data</h4>
      <pre className="code-block">{`# Stop DMS container
docker-compose stop dms

# Restore mail data
tar -xzf dms-backup_YYYYMMDD.tar.gz -C /

# Restore configuration
tar -xzf dms-config_YYYYMMDD.tar.gz -C /

# Start DMS container
docker-compose start dms`}</pre>
    </div>

    <div className="doc-card">
      <h3>📊 Monitoring & Maintenance</h3>
      
      <h4>Daily Checks</h4>
      <ul>
        <li>Check mail queue: <code>docker exec dms postqueue -p</code></li>
        <li>Review logs: <code>docker logs dms --tail=50</code></li>
        <li>Verify service health: <code>docker-compose ps dms</code></li>
      </ul>

      <h4>Weekly Checks</h4>
      <ul>
        <li>Review spam filter effectiveness</li>
        <li>Check disk usage: <code>du -sh /home/YOUR_SSH_USER/cranfordpub/dms-data</code></li>
        <li>Verify certificate validity</li>
      </ul>

      <h4>Monthly Maintenance</h4>
      <ul>
        <li>Backup mail data and configuration</li>
        <li>Update DMS image: <code>docker-compose pull dms && docker-compose up -d dms</code></li>
        <li>Check blacklist status</li>
        <li>Review user quotas</li>
      </ul>
    </div>
  </div>
);

const DeveloperSection = () => (
  <div className="doc-section">
    <h2>👨‍💻 Developer Contact Information</h2>
    
    <div className="doc-card">
      <h3>🎭 Meet the Wizard Behind the Curtain</h3>
      <div className="developer-profile">
        <div className="developer-info">
          <h4>Markus Van Kempen</h4>
          <p className="developer-title">Full Stack Developer & System Architect (aka Code Whisperer & Bug Exterminator)</p>
          <p>When he's not wrestling with Docker containers or explaining to SQLite why it shouldn't lock up under pressure, Markus is the lead developer and architect of the Cranford Publications Web Application. He turns coffee into code and chaos into beautiful, functional systems.</p>
          <p><em>"I don't always write documentation, but when I do, I make it funny."</em> - Markus, probably</p>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>📫 How to Reach This Magical Being</h3>
      <div className="contact-grid">
        <div className="contact-item">
          <span className="contact-icon">📧</span>
          <div className="contact-details">
            <strong>Email</strong>
            <a href="mailto:markus.van.kempen@gmail.com">markus.van.kempen@gmail.com</a>
            <p><small>Preferred communication method. Response time: Faster than a database query, slower than instant noodles.</small></p>
          </div>
        </div>
        <div className="contact-item">
          <span className="contact-icon">💼</span>
          <div className="contact-details">
            <strong>LinkedIn</strong>
            <a href="https://www.linkedin.com/in/markusvankempen/" target="_blank" rel="noopener noreferrer">
              linkedin.com/in/markusvankempen
            </a>
            <p><small>Connect for professional networking, code reviews, and sharing memes about JavaScript.</small></p>
          </div>
        </div>
        <div className="contact-item">
          <span className="contact-icon">🌐</span>
          <div className="contact-details">
            <strong>Portfolio / Blog</strong>
            <a href="https://markusvankempen.wordpress.com/" target="_blank" rel="noopener noreferrer">
              markusvankempen.wordpress.com
            </a>
            <p><small>Where code meets creativity and technical articles meet terrible puns.</small></p>
          </div>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>🛠️ What Markus Actually Does (According to Markus)</h3>
      <div className="feature-grid">
        <div className="feature-item">
          <h4>🎨 Frontend Development</h4>
          <p><em>"Making things look pretty and actually work"</em></p>
          <ul>
            <li>React.js application architecture (because vanilla JS is too vanilla)</li>
            <li>Responsive UI/UX design (yes, it works on your phone too)</li>
            <li>Component library development (DRY principle: Don't Repeat Yourself, or cry when debugging)</li>
            <li>State management implementation (keeping track of who clicked what, when, and why)</li>
          </ul>
        </div>
        <div className="feature-item">
          <h4>⚙️ Backend Development</h4>
          <p><em>"Making the magic happen behind the scenes"</em></p>
          <ul>
            <li>Node.js/Express API design (RESTful, but sometimes restless)</li>
            <li>Database schema design (normalization is a suggestion, not a rule... just kidding, it's a rule)</li>
            <li>Authentication system (keeping the bad guys out, one JWT token at a time)</li>
            <li>RESTful API implementation (because SOAP is for washing hands)</li>
          </ul>
        </div>
        <div className="feature-item">
          <h4>🐳 Infrastructure & DevOps</h4>
          <p><em>"Where containers go to play nicely together"</em></p>
          <ul>
            <li>Docker containerization (it works on my machine™, now in a box)</li>
            <li>Production deployment setup (turning "it works locally" into "it works everywhere")</li>
            <li>Mail server configuration (DMS - because emails shouldn't get lost in the void)</li>
            <li>SSL/TLS certificate management (keeping things secure and Let's Encrypt happy)</li>
          </ul>
        </div>
        <div className="feature-item">
          <h4>🔧 System Integration</h4>
          <p><em>"Making all the pieces talk to each other without fighting"</em></p>
          <ul>
            <li>Traefik reverse proxy setup (the traffic director nobody asked for but everyone needs)</li>
            <li>PostgreSQL database integration (the future is relational, apparently)</li>
            <li>Node-RED automation flows (because clicking buttons manually is so 2020)</li>
            <li>Rainloop webmail integration (webmail that doesn't make you cry)</li>
          </ul>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>📚 Technologies Markus Claims to Know</h3>
      <p><em>Disclaimer: Actual mastery may vary. Google and Stack Overflow are always just one tab away.</em></p>
      <div className="tech-stack">
        <div className="tech-category">
          <h4>Frontend Technologies</h4>
          <p><small>The stuff you see and hopefully like</small></p>
          <div className="tech-tags">
            <span className="tech-tag">React.js</span>
            <span className="tech-tag">JavaScript (ES6+)</span>
            <span className="tech-tag">HTML5</span>
            <span className="tech-tag">CSS3</span>
            <span className="tech-tag">React Router</span>
            <span className="tech-tag">Responsive Design</span>
          </div>
        </div>
        
        <div className="tech-category">
          <h4>Backend Technologies</h4>
          <p><small>Where the real magic happens (or breaks)</small></p>
          <div className="tech-tags">
            <span className="tech-tag">Node.js</span>
            <span className="tech-tag">Express.js</span>
            <span className="tech-tag">RESTful APIs</span>
            <span className="tech-tag">SQLite</span>
            <span className="tech-tag">PostgreSQL</span>
            <span className="tech-tag">JWT Authentication</span>
          </div>
        </div>
        
        <div className="tech-category">
          <h4>DevOps & Infrastructure</h4>
          <p><small>Making computers do things automatically so Markus doesn't have to</small></p>
          <div className="tech-tags">
            <span className="tech-tag">Docker</span>
            <span className="tech-tag">Docker Compose</span>
            <span className="tech-tag">Traefik</span>
            <span className="tech-tag">Linux (Ubuntu)</span>
            <span className="tech-tag">Nginx</span>
            <span className="tech-tag">Let's Encrypt</span>
            <span className="tech-tag">Docker Mail Server</span>
          </div>
        </div>
        
        <div className="tech-category">
          <h4>Tools & Platforms</h4>
          <p><small>The things that make development less painful</small></p>
          <div className="tech-tags">
            <span className="tech-tag">Git</span>
            <span className="tech-tag">VS Code</span>
            <span className="tech-tag">Node-RED</span>
            <span className="tech-tag">Portainer</span>
            <span className="tech-tag">pgAdmin</span>
            <span className="tech-tag">Postman</span>
          </div>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>🎯 Project Timeline & Milestones (The Journey So Far)</h3>
      <p><em>A story of triumph, tears, and "why did I think that was a good idea?" moments.</em></p>
      <div className="timeline">
        <div className="timeline-item">
          <div className="timeline-marker">✅</div>
          <div className="timeline-content">
            <h4>Phase 1: Foundation (The "Let's Build This Thing" Phase)</h4>
            <p><strong>Status:</strong> Complete (after only 47 rewrites)</p>
            <ul>
              <li>Application architecture design (boxes and arrows, then actual code)</li>
              <li>Database schema implementation (normalized, because chaos is not a database design pattern)</li>
              <li>Core API development (RESTful, with a healthy dose of RESTless nights)</li>
              <li>Authentication system (because security is kind of important)</li>
            </ul>
          </div>
        </div>
        
        <div className="timeline-item">
          <div className="timeline-marker">✅</div>
          <div className="timeline-content">
            <h4>Phase 2: Frontend Development (The "This Should Work" Phase)</h4>
            <p><strong>Status:</strong> Complete (mostly. CSS is hard.)</p>
            <ul>
              <li>React component library (reusable, like Legos, but for code)</li>
              <li>Admin dashboard (where admins go to admin things)</li>
              <li>Content management interface (making content management less painful)</li>
              <li>User-facing pages (the pretty stuff users actually see)</li>
            </ul>
          </div>
        </div>
        
        <div className="timeline-item">
          <div className="timeline-marker">✅</div>
          <div className="timeline-content">
            <h4>Phase 3: Infrastructure (The "Why is Docker So Confusing?" Phase)</h4>
            <p><strong>Status:</strong> Complete (after many "it works on my machine" moments)</p>
            <ul>
              <li>Docker containerization (putting things in boxes so they can't escape)</li>
              <li>Production VPS setup (where the magic lives on the internet)</li>
              <li>Mail server configuration (because emails should actually arrive)</li>
              <li>SSL/TLS automation (keeping hackers at bay, one certificate at a time)</li>
            </ul>
          </div>
        </div>
        
        <div className="timeline-item">
          <div className="timeline-marker">🔄</div>
          <div className="timeline-content">
            <h4>Phase 4: Enhancement & Optimization (The "It Works, But..." Phase)</h4>
            <p><strong>Status:</strong> In Progress (also known as "the never-ending story")</p>
            <ul>
              <li>Performance optimization (making things fast because slow is not a feature)</li>
              <li>PostgreSQL migration planning (SQLite, we had good times, but it's time to move on)</li>
              <li>Feature enhancements (because users always want "just one more thing")</li>
              <li>Documentation expansion (including making it funny, apparently)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>💡 Need Help? Markus Has Your Back (Or At Least Your Email)</h3>
      <p>For technical support, bug reports, feature requests, or existential questions about why your code isn't working:</p>
      
      <div className="support-options">
        <div className="support-option">
          <h4>📧 Email Support</h4>
          <p>For detailed inquiries, technical issues, or project discussions. Also accepts pizza orders, but not payment.</p>
          <a href="mailto:markus.van.kempen@gmail.com" className="contact-button">
            Email Markus Van Kempen
          </a>
          <p><small>Fun fact: Markus responds faster to emails with subject lines that include emojis. 🎯</small></p>
        </div>
        
        <div className="support-option">
          <h4>💼 LinkedIn Connection</h4>
          <p>Connect for professional networking, code reviews, and sharing developer memes that only other developers find funny.</p>
          <a href="https://www.linkedin.com/in/markusvankempen/" target="_blank" rel="noopener noreferrer" className="contact-button">
            Connect on LinkedIn
          </a>
        </div>
        
        <div className="support-option">
          <h4>📖 Portfolio & Blog</h4>
          <p>View more projects, technical articles, and development insights that may or may not include terrible puns.</p>
          <a href="https://markusvankempen.wordpress.com/" target="_blank" rel="noopener noreferrer" className="contact-button">
            Visit Portfolio
          </a>
        </div>
      </div>
    </div>

    <div className="doc-card important">
      <h3>⚠️ Important Notes (Read These or Suffer the Consequences)</h3>
      <ul>
        <li><strong>Response Time:</strong> Typically within 24-48 hours for email inquiries. If it's an emergency, send pizza. Or donuts. Both work.</li>
        <li><strong>Documentation:</strong> Please refer to this documentation first for common questions. Markus spent time writing it, so use it. (He's watching.)</li>
        <li><strong>Bug Reports:</strong> Include detailed steps to reproduce, error messages, and screenshots. "It's broken" is not a bug report, it's a mystery.</li>
        <li><strong>Feature Requests:</strong> Describe the desired functionality and use case in detail. "Make it cooler" is not actionable. (But nice try.)</li>
        <li><strong>System Access:</strong> Never share credentials or sensitive information via email. Markus doesn't want your passwords. Really.</li>
        <li><strong>Coffee Preferences:</strong> Dark roast preferred, but he'll accept any caffeinated beverage in a crisis.</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🙏 Acknowledgments (The Thank You Section)</h3>
      <p>This project was developed for Cranford Publications to provide a modern, efficient web presence for sharing traditional music content, managing products, and engaging with the community. Also, because Markus thought it would be fun. (It mostly was.)</p>
      
      <p><strong>Special thanks to:</strong></p>
      <ul>
        <li>The Cranford Publications team for their vision, collaboration, and patience when things didn't work the first time (or the second, or third...)</li>
        <li>The open-source community for the excellent tools and frameworks used in this project. You're the real MVPs.</li>
        <li>All contributors who have provided feedback and suggestions. Even the ones Markus initially disagreed with (he was probably wrong).</li>
        <li>Coffee, for existing and making long coding sessions possible.</li>
        <li>Stack Overflow, for answering questions Markus didn't even know he had.</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🎉 Fun Facts About Markus</h3>
      <ul>
        <li>🏃 Markus runs on coffee and code, in that order</li>
        <li>🐛 He's debugged more issues than he cares to admit (or remember)</li>
        <li>📚 Loves reading technical documentation (yes, he's that kind of person)</li>
        <li>🎸 Has been known to play music when not writing code (allegedly)</li>
        <li>☕ Considers "too much coffee" a myth perpetuated by non-developers</li>
        <li>💻 Believes that the best code is code that doesn't need comments (but adds them anyway because reality is harsh)</li>
        <li>🔧 Once spent 3 hours debugging a typo (we've all been there)</li>
        <li>📱 Can be found on <a href="https://www.linkedin.com/in/markusvankempen/" target="_blank" rel="noopener noreferrer">LinkedIn</a> sharing development insights, project updates, and the occasional tech meme</li>
      </ul>
    </div>
  </div>
);

// End User Guide Sections
const OrderProcessingSection = () => (
  <div className="doc-section">
    <h2>📦 Order Processing</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>This guide explains how to process and manage customer orders in the admin panel.</p>
    </div>

    <div className="doc-card">
      <h3>Accessing Orders</h3>
      <ol>
        <li>Navigate to <strong>Admin Dashboard</strong> → <strong>Order Management</strong></li>
        <li>You'll see a list of all orders with key information:
          <ul>
            <li>Order number</li>
            <li>Customer name and email</li>
            <li>Order date</li>
            <li>Total amount</li>
            <li><strong>Proc. fee</strong> — Stripe/PayPal processing fee (not shown on customer invoices)</li>
            <li><strong>Net payout</strong> — amount deposited after processor fees</li>
            <li>Payment status</li>
            <li>Order status</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Viewing Order Details</h3>
      <ol>
        <li>Click on any order to view full details</li>
        <li>The order detail view shows:
          <ul>
            <li><strong>Customer Information:</strong> Name, email, phone, shipping address</li>
            <li><strong>Order Items:</strong> Products, quantities, prices</li>
            <li><strong>Payment Information:</strong> Payment method (Stripe/PayPal), transaction ID</li>
            <li><strong>Shipping Details:</strong> Shipping method, tracking number (if available)</li>
            <li><strong>Order Totals:</strong> Subtotal, tax, shipping, total</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Processing New Orders</h3>
      <ol>
        <li><strong>Review the Order:</strong> Check customer information and items</li>
        <li><strong>Verify Payment:</strong> Confirm payment was received (see Payment Dashboards guide)</li>
        <li><strong>Check Inventory:</strong> Ensure all items are in stock</li>
        <li><strong>Update Status:</strong> Change order status to "processing" or "shipped" as appropriate</li>
        <li><strong>Add Tracking:</strong> If shipped, add tracking number in the order details</li>
        <li><strong>Send Confirmation:</strong> Customer receives automatic email notifications</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Processing fees (tax / accounting)</h3>
      <p>
        Stripe and PayPal deduct <strong>processing fees</strong> from each card/PayPal payment. These are visible in the orders table
        (<strong>Proc. fee</strong> and <strong>Net payout</strong> columns) and in each order&apos;s payment breakdown — but they are
        <strong> not</strong> printed on customer invoices.
      </p>
      <ol>
        <li>Filter orders by date range (e.g. a tax year or quarter)</li>
        <li>Open <strong>Export Orders</strong> → <strong>Processing fees (tax) — all shown</strong></li>
        <li>The CSV includes gross amount, processing fee, refunds, net payout, provider, and transaction IDs, plus a totals row for online payments</li>
      </ol>
      <p>Check/cash/mail orders appear in the export with a note and no processor fee columns.</p>
    </div>

    <div className="doc-card important">
      <h3>💡 Tips</h3>
      <ul>
        <li>Orders are automatically created when customers complete checkout</li>
        <li>Payment verification happens automatically for Stripe and PayPal</li>
        <li>Always verify payment before shipping products</li>
        <li>Update order status promptly to keep customers informed</li>
        <li>For check, cash, or wholesale mail orders, see <strong>Mail / Check Orders</strong> in this documentation</li>
      </ul>
    </div>
  </div>
);

const MailOrderSection = () => (
  <div className="doc-section">
    <h2>✉️ Mail / Check Orders</h2>

    <div className="doc-card">
      <h3>Overview</h3>
      <p>
        Use this flow when customers pay by <strong>check</strong>, <strong>cash</strong>, or <strong>mail order</strong>
        instead of Stripe/PayPal — including <strong>wholesale</strong> buyers with tier discounts.
      </p>
      <p>
        Open the same form from either:
      </p>
      <ul>
        <li><strong>Admin → Order Management</strong> → <strong>✉️ New mail / check order</strong></li>
        <li><strong>Checkout</strong> (admin) → shipping step → <strong>✉️ New mail / check order</strong> (prefills cart)</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Quick workflow</h3>
      <ol>
        <li>Click <strong>✉️ New mail / check order</strong></li>
        <li><strong>Find customer</strong> — search by name or email (optional; prefills wholesaler accounts)</li>
        <li>Choose order type: <strong>Live mail order</strong> or <strong>Test mail order</strong></li>
        <li>Choose pricing: <strong>Customer price</strong> or <strong>🏢 Wholesale pricing</strong></li>
        <li>Add products (search by name/SKU), quantities, shipping address, and tax</li>
        <li>Payment method: Check / Cash / Invoice / etc.</li>
        <li><strong>Create order &amp; email invoice</strong></li>
        <li>When payment arrives → open order → <strong>✅ Mark payment received</strong></li>
        <li>Ship and update order status (processing → shipped)</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Pricing</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Mode</th>
            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>When to use</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}><strong>Customer price</strong></td>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>Regular catalog prices for retail buyers</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}><strong>🏢 Wholesale pricing</strong></td>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>Standard tier from each product&apos;s admin wholesale settings (🏢 Wholesale Pricing)</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}><strong>Manual discount %</strong></td>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>When a product has no wholesale config — enter a discount % on that line</td>
          </tr>
        </tbody>
      </table>
      <p style={{ marginTop: '12px', fontSize: '0.9em', color: '#6b7280' }}>
        Wholesale lines on the invoice are marked with <strong>🏢 Wholesale</strong>.
        Mail orders use the standard wholesale tier from product settings (minimum quantity rules are not enforced on mail orders).
      </p>
    </div>

    <div className="doc-card">
      <h3>Live vs test mail orders</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Mode</th>
            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>When to use</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}><strong>Live mail order</strong></td>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>Real customer orders — default</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}><strong>Test mail order</strong></td>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>Practice only — order # <code>FID-MAIL-TEST-…</code>, badge <strong>🧪 Test mail</strong>, invoice subject <strong>[TEST]</strong></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="doc-card">
      <h3>Order &amp; payment status</h3>
      <ul>
        <li>New mail orders start as <strong>Order status: pending</strong> and <strong>Payment status: pending_payment</strong></li>
        <li>Order numbers look like <code>FID-MAIL-…</code></li>
        <li>Do <strong>not</strong> ship physical items until payment is confirmed</li>
        <li>Use <strong>Mark payment received</strong> after the check clears or cash is in hand</li>
        <li>Digital downloads follow the same rules as online orders (paid + completed)</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Email invoice &amp; check instructions</h3>
      <ol>
        <li>On create, check <strong>Email invoice to customer</strong></li>
        <li>For checks, also check <strong>Include check / mail payment instructions</strong></li>
        <li>To resend: select order → <strong>Invoice → Email</strong></li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Cart inquiries vs mail orders</h3>
      <p>
        <strong>Email my cart</strong> at checkout (when card/PayPal unavailable) sends a cart inquiry — it does
        <strong> not</strong> create an order. Inquiries appear at the top of <strong>Admin → Orders</strong>.
        After Paul reviews one, use <strong>Create mail order</strong> on that row (or <strong>New mail / check order</strong>)
        for tracking and invoicing. Use <strong>Archive</strong> to hide a finished inquiry from the main list
        (Show archived to restore).
      </p>
    </div>

    <div className="doc-card important">
      <h3>💡 Tips</h3>
      <ul>
        <li>Use <strong>Find customer</strong> on wholesaler emails to auto-suggest wholesale pricing</li>
        <li>CSV <strong>Import Orders</strong> is for bulk/history — use <strong>New mail / check order</strong> for daily work</li>
        <li>Refunds via Stripe/PayPal do not apply to check-only orders — handle those manually</li>
      </ul>
    </div>
  </div>
);

const PaymentFeesTaxExportSection = () => (
  <div className="doc-section">
    <h2>🧾 Payment Processing Fees (Tax / Accounting)</h2>

    <div className="doc-card">
      <h3>Overview</h3>
      <p>
        Stripe and PayPal deduct a <strong>processing fee</strong> from each card/PayPal payment. These fees are visible
        in admin and exportable for tax and bookkeeping — but are <strong>not</strong> printed on customer invoices.
      </p>
    </div>

    <div className="doc-card">
      <h3>Orders list columns</h3>
      <ul>
        <li><strong>Proc. fee</strong> — processor fee for that order (Stripe/PayPal only)</li>
        <li><strong>Net payout</strong> — amount deposited after fees and refunds</li>
      </ul>
      <p>Check, cash, and mail orders show <strong>—</strong> (no online processor).</p>
    </div>

    <div className="doc-card">
      <h3>Export CSV for tax</h3>
      <ol>
        <li>Filter orders by date (e.g. tax year or quarter)</li>
        <li><strong>Export Orders</strong> → <strong>💳 Processing fees (tax) — all shown</strong></li>
        <li>CSV includes gross, processing fee, refunds, net payout, provider, transaction IDs, and a totals row</li>
      </ol>
    </div>

    <div className="doc-card important">
      <h3>Transaction fee vs processing fee</h3>
      <ul>
        <li><strong>Transaction fee</strong> (checkout) — optional % charged to the customer; configured in shipping/flat-rate settings</li>
        <li><strong>Processing fee</strong> (this section) — fee Stripe/PayPal deducts from <em>your</em> payout; admin-only</li>
      </ul>
    </div>
  </div>
);

const RefundsSection = () => (
  <div className="doc-section">
    <h2>💵 Processing Refunds</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>The system supports both <strong>full refunds</strong> and <strong>partial refunds</strong> for orders paid via Stripe or PayPal.</p>
    </div>

    <div className="doc-card">
      <h3>Accessing Refund Options</h3>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Order Management</strong></li>
        <li>Open the order you want to refund</li>
        <li>Click the <strong>"💵 Process Refund"</strong> button</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Refund Process</h3>
      <ol>
        <li><strong>Refund Modal Opens:</strong> You'll see:
          <ul>
            <li>Order details (order number, payment method, total amount)</li>
            <li>Refund amount input field</li>
            <li>Quick buttons: <strong>50%</strong> or <strong>100% (Full)</strong></li>
            <li>Refund reason dropdown</li>
          </ul>
        </li>
        <li><strong>Enter Refund Amount:</strong>
          <ul>
            <li>Type the amount manually, or</li>
            <li>Click <strong>50%</strong> for half refund, or</li>
            <li>Click <strong>100% (Full)</strong> for complete refund</li>
          </ul>
        </li>
        <li><strong>Select Reason:</strong> Choose from:
          <ul>
            <li>Shipping refund (default)</li>
            <li>Requested by customer</li>
            <li>Duplicate order</li>
            <li>Fraudulent transaction</li>
          </ul>
        </li>
        <li><strong>Confirm Refund:</strong> Click the refund button</li>
        <li><strong>System Processing:</strong>
          <ul>
            <li>Refund is processed via Stripe or PayPal API</li>
            <li>Order status updates automatically</li>
            <li>Customer receives email notification</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Refund Types</h3>
      
      <h4>Full Refund</h4>
      <ul>
        <li>Refunds the entire order amount</li>
        <li>Order status changes to <strong>"refunded"</strong></li>
        <li>Customer receives full refund email</li>
        <li>Refund appears in customer's account in 5-10 business days</li>
      </ul>

      <h4>Partial Refund</h4>
      <ul>
        <li>Refunds a portion of the order amount</li>
        <li>Order status changes to <strong>"partially_refunded"</strong></li>
        <li>Customer receives partial refund email with amount</li>
        <li>You can process multiple partial refunds until the full amount is refunded</li>
      </ul>
    </div>

    <div className="doc-card warning">
      <h3>⚠️ Important Notes</h3>
      <ul>
        <li><strong>Refunds cannot be undone</strong> - verify the amount before confirming</li>
        <li>Refunds are processed immediately via payment provider API</li>
        <li>Customer notifications are sent automatically</li>
        <li>Refund processing time: 5-10 business days to appear in customer's account</li>
        <li>For Stripe: Refunds go back to the original payment method</li>
        <li>For PayPal: Refunds go back to the PayPal account used</li>
      </ul>
    </div>
  </div>
);

const PaymentDashboardsSection = () => (
  <div className="doc-section">
    <h2>💳 Accessing Payment Dashboards</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>To verify transactions and manage payments, you need to access the Stripe and PayPal dashboards directly. These are separate from the admin panel and provide detailed transaction information.</p>
    </div>

    <div className="doc-card">
      <h3>Stripe Dashboard</h3>
      <ol>
        <li><strong>Access:</strong> Go to <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">https://dashboard.stripe.com</a></li>
        <li><strong>Login:</strong> Use your Stripe account credentials</li>
        <li><strong>Navigate to Payments:</strong> Click "Payments" in the left sidebar</li>
        <li><strong>Find Transactions:</strong>
          <ul>
            <li>Search by customer email, order number, or payment intent ID</li>
            <li>Filter by date, amount, or status</li>
          </ul>
        </li>
        <li><strong>View Transaction Details:</strong>
          <ul>
            <li>Payment amount and currency</li>
            <li>Customer information</li>
            <li>Payment method (card type, last 4 digits)</li>
            <li>Transaction status (succeeded, failed, refunded)</li>
            <li>Refund history (if any)</li>
            <li>Payment intent ID (matches order in admin panel)</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>PayPal Dashboard</h3>
      <ol>
        <li><strong>Access:</strong> Go to <a href="https://www.paypal.com/businessmanage/account/home" target="_blank" rel="noopener noreferrer">https://www.paypal.com/businessmanage/account/home</a></li>
        <li><strong>Login:</strong> Use your PayPal business account credentials</li>
        <li><strong>Navigate to Transactions:</strong> Click "Activity" or "Transactions"</li>
        <li><strong>Find Transactions:</strong>
          <ul>
            <li>Search by customer email, transaction ID, or order number</li>
            <li>Filter by date range, status, or type</li>
          </ul>
        </li>
        <li><strong>View Transaction Details:</strong>
          <ul>
            <li>Payment amount and currency</li>
            <li>Customer name and email</li>
            <li>Transaction ID (matches order in admin panel)</li>
            <li>Payment status (completed, pending, refunded)</li>
            <li>Refund information (if any)</li>
            <li>Shipping address</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Verifying Transactions</h3>
      <ol>
        <li><strong>In Admin Panel:</strong> Note the payment intent ID (Stripe) or transaction ID (PayPal) from the order</li>
        <li><strong>In Payment Dashboard:</strong> Search for that ID</li>
        <li><strong>Compare Details:</strong> Verify:
          <ul>
            <li>Amount matches the order total</li>
            <li>Customer information matches</li>
            <li>Payment status is "completed" or "succeeded"</li>
            <li>Date matches the order date</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card important">
      <h3>💡 Tips</h3>
      <ul>
        <li>Bookmark both dashboards for quick access</li>
        <li>Use the search function to quickly find specific transactions</li>
        <li>Export transaction reports for accounting purposes</li>
        <li>Check for pending payments that may need attention</li>
        <li>Review refund history regularly to track customer service issues</li>
      </ul>
    </div>

    <div className="doc-card warning">
      <h3>⚠️ Security Notes</h3>
      <ul>
        <li>Never share your dashboard login credentials</li>
        <li>Use two-factor authentication if available</li>
        <li>Log out when finished, especially on shared computers</li>
        <li>Contact Stripe/PayPal support if you notice suspicious activity</li>
      </ul>
    </div>
  </div>
);

const OrderStatusSection = () => (
  <div className="doc-section">
    <h2>🔄 Managing Order Status</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>Order status helps track where each order is in the fulfillment process. You can update statuses manually to keep customers informed.</p>
    </div>

    <div className="doc-card">
      <h3>Available Order Statuses</h3>
      <ul>
        <li><strong>pending:</strong> Order received, payment pending</li>
        <li><strong>processing:</strong> Payment confirmed, order being prepared</li>
        <li><strong>shipped:</strong> Order has been shipped to customer</li>
        <li><strong>delivered:</strong> Order delivered to customer</li>
        <li><strong>completed:</strong> Order fulfilled and complete</li>
        <li><strong>cancelled:</strong> Order cancelled before fulfillment</li>
        <li><strong>refunded:</strong> Full refund processed</li>
        <li><strong>partially_refunded:</strong> Partial refund processed</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>How to Change Order Status</h3>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Order Management</strong></li>
        <li>Open the order you want to update</li>
        <li>Find the <strong>"Order Status"</strong> section</li>
        <li>Click the status dropdown menu</li>
        <li>Select the new status</li>
        <li>Click <strong>"Update Status"</strong> or <strong>"Save"</strong></li>
        <li>Customer may receive an email notification (depending on status change)</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Status Workflow Recommendations</h3>
      <ol>
        <li><strong>Order Placed:</strong> Status is automatically set to "pending"</li>
        <li><strong>Payment Confirmed:</strong> Change to "processing"</li>
        <li><strong>Order Packed:</strong> Keep as "processing" or change to "shipped" if tracking added</li>
        <li><strong>Shipped:</strong> Change to "shipped" and add tracking number</li>
        <li><strong>Delivered:</strong> Change to "delivered" when confirmed</li>
        <li><strong>Complete:</strong> Change to "completed" after delivery confirmation</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Adding Tracking Information</h3>
      <ol>
        <li>When changing status to "shipped", you'll see a tracking number field</li>
        <li>Enter the shipping carrier's tracking number</li>
        <li>Select the shipping carrier (if applicable)</li>
        <li>Save the order</li>
        <li>Customer receives email with tracking information</li>
      </ol>
    </div>

    <div className="doc-card important">
      <h3>💡 Best Practices</h3>
      <ul>
        <li>Update status promptly to keep customers informed</li>
        <li>Always add tracking numbers when available</li>
        <li>Use "processing" for orders being prepared</li>
        <li>Mark as "shipped" only after items leave your facility</li>
        <li>Review orders with "pending" status regularly</li>
      </ul>
    </div>
  </div>
);

const TuneCreationSection = () => (
  <div className="doc-section">
    <h2>🎵 Creating Tune Pages</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>Tune pages display musical notation, ABC code, audio playback, and related information. You can create new tune pages using templates.</p>
    </div>

    <div className="doc-card">
      <h3>Accessing Tune Creation</h3>
      <ol>
        <li>Navigate to <strong>Tunes</strong> page (from main navigation)</li>
        <li>Click <strong>"Create New Tune"</strong> button (admin/content creator only)</li>
        <li>Or go to <strong>Admin Dashboard</strong> → <strong>Page Management</strong></li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Creating a Tune via Template</h3>
      <ol>
        <li><strong>Select Template:</strong> Choose "Tune" template type</li>
        <li><strong>Basic Information:</strong>
          <ul>
            <li><strong>Title:</strong> Tune name (e.g., "The Irish Washerwoman")</li>
            <li><strong>Alternative Titles:</strong> Other names for the tune (comma-separated)</li>
            <li><strong>Tune Type:</strong> Jig, Reel, Hornpipe, etc.</li>
            <li><strong>Tune Region:</strong> Irish, Scottish, Cape Breton, etc.</li>
            <li><strong>Key:</strong> Musical key (e.g., D Major, G Major)</li>
            <li><strong>Difficulty:</strong> Beginner, Intermediate, Advanced</li>
          </ul>
        </li>
        <li><strong>ABC Notation:</strong>
          <ul>
            <li>Paste or type ABC notation code</li>
            <li>Or upload an ABC file (see Asset Upload guide)</li>
            <li>The system will render musical notation automatically</li>
          </ul>
        </li>
        <li><strong>Time Signature:</strong>
          <ul>
            <li>Supported values: <strong>2/4, 3/4, 4/4, 6/8, 9/8</strong></li>
            <li>Select from the dropdown — only standard Celtic time signatures are accepted</li>
          </ul>
        </li>
        <li><strong>Audio Files:</strong>
          <ul>
            <li>Upload MP3 files for audio playback</li>
            <li>Link to existing audio assets</li>
            <li>To remove an audio file, click the <strong>"Clear"</strong> button next to the MP3 field and save — the audio player will be removed from the tune page</li>
          </ul>
        </li>
        <li><strong>Additional Content:</strong>
          <ul>
            <li>Description/history of the tune</li>
            <li>Related products (books, CDs)</li>
            <li>Tags for searchability</li>
          </ul>
        </li>
        <li><strong>Save:</strong> Click "Save" or "Publish" to create the tune page</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Template Fields Explained</h3>
      <ul>
        <li><strong>Title:</strong> Main tune name displayed prominently</li>
        <li><strong>Alternative Titles:</strong> Helps users find tunes by different names</li>
        <li><strong>Tune Type:</strong> Used for filtering and categorization</li>
        <li><strong>Tune Region:</strong> Geographic/cultural origin</li>
        <li><strong>Key:</strong> Musical key signature</li>
        <li><strong>Time Signature:</strong> 2/4, 3/4, 4/4, 6/8, or 9/8 (restricted to standard Celtic meters; 9/8 for slip jigs)</li>
        <li><strong>Difficulty:</strong> Helps users find appropriate tunes</li>
        <li><strong>ABC Notation:</strong> Standard ABC music notation format</li>
        <li><strong>Audio:</strong> MP3 files for listening; use Clear then Save to remove an audio file</li>
        <li><strong>Description:</strong> Rich text content about the tune</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>💡 Tips</h3>
      <ul>
        <li>Use standard ABC notation format for best results</li>
        <li>Test ABC notation rendering before publishing</li>
        <li>Add multiple audio files for different tempos or instruments</li>
        <li>Link related products to increase sales</li>
        <li>Use descriptive tags for better searchability</li>
      </ul>
    </div>
  </div>
);

const AssetUploadSection = () => (
  <div className="doc-section">
    <h2>📁 Uploading Assets</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>The Asset Management system allows you to upload and organize images, audio files, ABC files, and other media for use throughout the site.</p>
    </div>

    <div className="doc-card">
      <h3>Accessing Asset Management</h3>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Asset Management</strong></li>
        <li>You'll see a list of existing assets and upload options</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Uploading Images</h3>
      <ol>
        <li>Click <strong>"Upload Asset"</strong> or <strong>"Upload Image"</strong></li>
        <li>Select image file(s) from your computer</li>
        <li>Supported formats: JPG, PNG, GIF, WebP</li>
        <li>Add metadata (optional):
          <ul>
            <li>Title/name</li>
            <li>Description</li>
            <li>Tags</li>
            <li>Category</li>
          </ul>
        </li>
        <li>Click <strong>"Upload"</strong></li>
        <li>Image is processed and added to asset library</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Uploading ABC Files</h3>
      <ol>
        <li>Click <strong>"Upload Asset"</strong></li>
        <li>Select <strong>"ABC File"</strong> or <strong>"Music Notation"</strong> category</li>
        <li>Choose your .abc or .txt file containing ABC notation</li>
        <li>Add metadata:
          <ul>
            <li>Tune name</li>
            <li>Composer (if known)</li>
            <li>Tags</li>
          </ul>
        </li>
        <li>Upload the file</li>
        <li>ABC file is stored and can be linked to tune pages</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Uploading Audio Files</h3>
      <ol>
        <li>Click <strong>"Upload Asset"</strong></li>
        <li>Select <strong>"Audio"</strong> category</li>
        <li>Choose MP3, WAV, or other audio files</li>
        <li>Add metadata:
          <ul>
            <li>Track name</li>
            <li>Artist/performer</li>
            <li>Album (if applicable)</li>
            <li>Tags</li>
          </ul>
        </li>
        <li>Upload the file</li>
        <li>Audio file is available for tune pages and products</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Bulk Upload</h3>
      <ol>
        <li>Use <strong>"Bulk Upload"</strong> option for multiple files</li>
        <li>Select multiple files at once (Ctrl/Cmd + Click)</li>
        <li>Files upload in sequence</li>
        <li>Add metadata to each file after upload</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Organizing Assets</h3>
      <ul>
        <li><strong>Search:</strong> Use search bar to find assets by name, tag, or type</li>
        <li><strong>Filter:</strong> Filter by category, file type, or date</li>
        <li><strong>Tags:</strong> Add tags to assets for easy organization</li>
        <li><strong>Categories:</strong> Organize assets into categories (Images, Audio, ABC, Documents)</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>💡 Best Practices</h3>
      <ul>
        <li>Use descriptive file names</li>
        <li>Add tags for easy searching</li>
        <li>Optimize images before uploading (reduce file size)</li>
        <li>Use consistent naming conventions</li>
        <li>Delete unused assets to save storage space</li>
        <li>Keep ABC files in standard format</li>
      </ul>
    </div>

    <div className="doc-card warning">
      <h3>⚠️ File Size Limits</h3>
      <ul>
        <li>Images: Recommended max 5MB per file</li>
        <li>Audio: Recommended max 10MB per file</li>
        <li>ABC files: Usually very small (&lt;100KB)</li>
        <li>Contact admin if you need to upload larger files</li>
      </ul>
    </div>
  </div>
);

const ABCTunesDisplaySection = () => (
  <div className="doc-section">
    <h2>🎼 How ABC Tunes Are Displayed</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>ABC notation is automatically rendered as musical notation and can be played as audio. This guide explains how ABC tunes appear to users.</p>
    </div>

    <div className="doc-card">
      <h3>Tune List View</h3>
      <ul>
        <li>Tunes are displayed in a grid or list format</li>
        <li>Each tune shows:
          <ul>
            <li>Tune title</li>
            <li>Tune type badge (Jig, Reel, etc.)</li>
            <li>Region badge (Irish, Scottish, etc.)</li>
            <li>Key signature</li>
            <li>Difficulty level</li>
            <li>Preview of notation (if available)</li>
          </ul>
        </li>
        <li>Users can filter by:
          <ul>
            <li>Region</li>
            <li>Tune type</li>
            <li>Difficulty</li>
            <li>Key</li>
            <li>Search term</li>
          </ul>
        </li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Tune Detail Page</h3>
      <p>When a user clicks on a tune, they see:</p>
      <ol>
        <li><strong>Header Section:</strong>
          <ul>
            <li>Tune title (large, prominent)</li>
            <li>Alternative titles (if any)</li>
            <li>Badges: Type, Region, Key, Difficulty</li>
          </ul>
        </li>
        <li><strong>Musical Notation:</strong>
          <ul>
            <li>Rendered sheet music from ABC code</li>
            <li>Interactive controls (zoom, print)</li>
            <li>Responsive display (adjusts to screen size)</li>
          </ul>
        </li>
        <li><strong>Audio Player:</strong>
          <ul>
            <li>Play/pause controls</li>
            <li>Playback speed adjustment</li>
            <li>Progress bar</li>
            <li>Multiple audio files (if available)</li>
          </ul>
        </li>
        <li><strong>ABC Code Section:</strong>
          <ul>
            <li>Editable ABC notation text</li>
            <li>Update button to re-render notation</li>
            <li>Copy to clipboard option</li>
          </ul>
        </li>
        <li><strong>Additional Information:</strong>
          <ul>
            <li>Description/history of the tune</li>
            <li>Related products (books, CDs)</li>
            <li>Tags and categories</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>ABC Rendering Features</h3>
      <ul>
        <li><strong>Automatic Rendering:</strong> ABC code is converted to musical notation automatically</li>
        <li><strong>Interactive Notation:</strong> Users can interact with the notation</li>
        <li><strong>Audio Synthesis:</strong> ABC code can be played as audio</li>
        <li><strong>Responsive Design:</strong> Notation adjusts to different screen sizes</li>
        <li><strong>Print Friendly:</strong> Users can print the notation</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>User Controls</h3>
      <ul>
        <li><strong>Playback Speed:</strong> Adjust tempo of audio playback</li>
        <li><strong>Zoom:</strong> Zoom in/out on notation</li>
        <li><strong>Print:</strong> Print notation as sheet music</li>
        <li><strong>Download:</strong> Download ABC file (if enabled)</li>
        <li><strong>Share:</strong> Share tune page link</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>💡 Technical Details</h3>
      <ul>
        <li>Uses ABC.js library for rendering</li>
        <li>Supports standard ABC notation format</li>
        <li>Audio is generated from ABC code using synthesizer</li>
        <li>Notation is rendered as SVG for crisp display</li>
        <li>Works on desktop and mobile devices</li>
      </ul>
    </div>
  </div>
);

const ProductCreationSection = () => (
  <div className="doc-section">
    <h2>🛍️ Creating Products</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>This guide explains how to create and manage products in your catalog, including books, CDs, sheet music, and bundles.</p>
    </div>

    <div className="doc-card">
      <h3>Accessing Product Management</h3>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Product Management</strong></li>
        <li>Click <strong>"Add New Product"</strong> button</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Basic Product Information</h3>
      <ol>
        <li><strong>Product Name:</strong> Enter the product title</li>
        <li><strong>Category:</strong> Select type (Book, CD, Sheet Music, Bundle, etc.)</li>
        <li><strong>Price:</strong> Enter price in CAD (e.g., 24.99)</li>
        <li><strong>SKU:</strong> Product code (auto-generated if left blank)</li>
        <li><strong>Inventory:</strong> Stock quantity</li>
        <li><strong>Description:</strong> Rich text description of the product</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Product Image</h3>
      <ol>
        <li>Click <strong>"Choose Image"</strong> or <strong>"Upload Image"</strong></li>
        <li>Either:
          <ul>
            <li>Select from existing assets in the library, or</li>
            <li>Upload a new image file</li>
          </ul>
        </li>
        <li>Image preview appears</li>
        <li>Recommended: Use high-quality images (at least 800x800px)</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Additional Product Details</h3>
      <ul>
        <li><strong>Author:</strong> Book author or composer name</li>
        <li><strong>Artist:</strong> Performer or artist name (for CDs)</li>
        <li><strong>Tags:</strong> Comma-separated tags for searchability</li>
        <li><strong>Related Products:</strong> Link to related items</li>
        <li><strong>Weight:</strong> Product weight for shipping calculations</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Shipping Configuration</h3>
      <ul>
        <li><strong>Shipping Enabled:</strong> Toggle whether product can be shipped</li>
        <li><strong>Minimum Shipping:</strong> Minimum shipping cost for this product</li>
        <li><strong>International Minimum:</strong> Minimum shipping for international orders</li>
        <li><strong>Weight:</strong> Product weight in kg (affects shipping cost)</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Tax Configuration</h3>
      <ul>
        <li><strong>Tax Type:</strong>
          <ul>
            <li><strong>Auto:</strong> System determines tax based on product category</li>
            <li><strong>GST:</strong> 5% GST only (books)</li>
            <li><strong>HST:</strong> 13-15% HST (varies by province)</li>
            <li><strong>Exempt:</strong> No tax</li>
          </ul>
        </li>
        <li><strong>Custom Tax Rate:</strong> Override with specific percentage if needed</li>
        <li><strong>Tax Notes:</strong> Internal notes about tax configuration</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Wholesale Pricing</h3>
      <ul>
        <li><strong>Standard Tier:</strong> Minimum quantity and discount %</li>
        <li><strong>Premium Tier:</strong> Higher quantity, better discount</li>
        <li><strong>Volume Tier:</strong> Highest quantity, maximum discount</li>
        <li>Example: 5+ items = 20% off, 10+ items = 30% off, 25+ items = 40% off</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Bundle Products</h3>
      <p>To create a bundle:</p>
      <ol>
        <li>Check <strong>"This is a Bundle"</strong></li>
        <li>Enter product IDs (comma-separated) that can be included</li>
        <li>Set minimum quantity for bundle discount</li>
        <li>Set discount percentage when minimum is reached</li>
        <li>Set maximum number of products customer can select</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Saving the Product</h3>
      <ol>
        <li>Review all information</li>
        <li>Click <strong>"Save Product"</strong> or <strong>"Publish"</strong></li>
        <li>Product appears in catalog immediately (unless archived)</li>
        <li>You can edit the product anytime</li>
      </ol>
    </div>

    <div className="doc-card important">
      <h3>💡 Tips</h3>
      <ul>
        <li>Use clear, descriptive product names</li>
        <li>Add detailed descriptions with keywords for SEO</li>
        <li>Use high-quality product images</li>
        <li>Set appropriate inventory levels</li>
        <li>Configure shipping and tax correctly</li>
        <li>Link related products to increase sales</li>
        <li>Use tags for better searchability</li>
      </ul>
    </div>
  </div>
);

const ShippingConfigSection = () => (
  <div className="doc-section">
    <h2>🚚 Shipping Configuration</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>Shipping rates are configurable through the admin panel. You can set different rates for Canada, USA, and International orders, plus free shipping thresholds.</p>
    </div>

    <div className="doc-card">
      <h3>Accessing Shipping Settings</h3>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Shipping Settings</strong></li>
        <li>You'll see three shipping zones:
          <ul>
            <li>🇨🇦 <strong>Canada</strong></li>
            <li>🇺🇸 <strong>United States</strong></li>
            <li>🌍 <strong>International</strong> (all other countries)</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Configuring Shipping Rates</h3>
      <ol>
        <li>Click <strong>"✏️ Edit Rates"</strong> on the zone you want to configure</li>
        <li>You'll see fields for:
          <ul>
            <li><strong>Standard Shipping:</strong> Base shipping cost (e.g., 8.99)</li>
            <li><strong>Expedited Shipping:</strong> Faster delivery option (e.g., 15.99)</li>
            <li><strong>Express Shipping:</strong> Fastest delivery option (e.g., 24.99)</li>
            <li><strong>Free Shipping Threshold:</strong> Order amount for free shipping (e.g., 50.00)</li>
          </ul>
        </li>
        <li>Enter rates in <strong>Canadian Dollars (CAD)</strong></li>
        <li>Click <strong>"✅ Save Changes"</strong></li>
        <li>Changes take effect immediately</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Enabling/Disabling Shipping Zones</h3>
      <ol>
        <li>In the shipping zone card, toggle <strong>"Enabled"</strong></li>
        <li>Disabled zones won't appear as shipping options at checkout</li>
        <li>Use this to temporarily disable shipping to certain regions</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>How Shipping is Calculated</h3>
      <ol>
        <li><strong>Check Free Shipping:</strong> If order total ≥ free shipping threshold, shipping is free</li>
        <li><strong>Base Rate:</strong> Uses the standard/expedited/express rate for the selected zone</li>
        <li><strong>Weight-Based Charges:</strong> Additional charges may apply for heavy items</li>
        <li><strong>Product Minimums:</strong> Some products have minimum shipping costs</li>
        <li><strong>Final Cost:</strong> System calculates the highest applicable rate</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Where Shipping Settings Are Stored</h3>
      <ul>
        <li><strong>Database Table:</strong> <code>shipping_settings</code></li>
        <li><strong>Location:</strong> Stored in SQLite database</li>
        <li><strong>API Endpoint:</strong> <code>/api/shipping-settings</code></li>
        <li><strong>Frontend Config:</strong> <code>frontend/src/utils/shippingConfig.js</code> (fallback values)</li>
      </ul>
      <p><strong>Note:</strong> Database values override hardcoded values in the config file.</p>
    </div>

    <div className="doc-card">
      <h3>Default Shipping Rates</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
        <thead>
          <tr style={{ backgroundColor: 'var(--color-surface)' }}>
            <th style={{ padding: '8px', border: '1px solid var(--color-border)' }}>Zone</th>
            <th style={{ padding: '8px', border: '1px solid var(--color-border)' }}>Standard</th>
            <th style={{ padding: '8px', border: '1px solid var(--color-border)' }}>Expedited</th>
            <th style={{ padding: '8px', border: '1px solid var(--color-border)' }}>Express</th>
            <th style={{ padding: '8px', border: '1px solid var(--color-border)' }}>Free Over</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>🇨🇦 Canada</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$8.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$15.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$24.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$50.00</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>🇺🇸 USA</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$5.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$12.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$19.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$75.00</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>🌍 International</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$24.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$35.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$49.99</td>
            <td style={{ padding: '8px', border: '1px solid var(--color-border)' }}>$200.00</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="doc-card">
      <h3>Changing Shipping Rates</h3>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Shipping Settings</strong></li>
        <li>Click <strong>"✏️ Edit Rates"</strong> on the zone</li>
        <li>Update the rate values</li>
        <li>Click <strong>"✅ Save Changes"</strong></li>
        <li>New rates apply to all future orders immediately</li>
      </ol>
      <p><strong>Note:</strong> Existing orders keep their original shipping rates.</p>
    </div>

    <div className="doc-card important">
      <h3>💡 Tips</h3>
      <ul>
        <li>Review shipping rates regularly to ensure they cover costs</li>
        <li>Test checkout with different order amounts to verify free shipping thresholds</li>
        <li>Consider weight-based charges for heavy products</li>
        <li>Update rates before peak seasons (holidays, sales)</li>
        <li>Keep international rates competitive but profitable</li>
      </ul>
    </div>

    <div className="doc-card warning">
      <h3>⚠️ Important Notes</h3>
      <ul>
        <li>All rates are in <strong>Canadian Dollars (CAD)</strong></li>
        <li>Changes take effect immediately for new orders</li>
        <li>Free shipping threshold is based on order subtotal (before tax)</li>
        <li>Product-specific minimum shipping costs override zone rates</li>
        <li>Contact support if you need to adjust rates for existing orders</li>
      </ul>
    </div>
  </div>
);

const PageCreationSection = () => (
  <div className="doc-section">
    <h2>📄 Page Creation & Management</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>Creating pages in the system is a <strong>two-step process</strong>: first you save the page metadata (title, description, type), then you edit the HTML content. This guide walks you through both steps and explains how page management works.</p>
    </div>

    <div className="doc-card important">
      <h3>📋 The Two-Step Process</h3>
      <ol>
        <li><strong>Step 1: Save Metadata</strong> - Create the page with title, description, and page type</li>
        <li><strong>Step 2: Edit HTML Content</strong> - Add and edit the actual page content using the rich text editor</li>
      </ol>
      <p><strong>Why two steps?</strong> The page must exist in the database before you can edit its content. Saving metadata creates the page record, then you can add content.</p>
    </div>

    <div className="doc-card">
      <h3>Step 1: Creating a Page (Metadata)</h3>
      
      <h4>Accessing Page Creation</h4>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Page Management</strong></li>
        <li>Click <strong>"Create New Page"</strong> button</li>
        <li>You'll see the page creation form</li>
      </ol>

      <h4>Filling in Page Metadata</h4>
      <ul>
        <li><strong>Page ID:</strong> 
          <ul>
            <li>Unique identifier for the page (e.g., "my-custom-page")</li>
            <li>Must be lowercase letters, numbers, and hyphens only</li>
            <li>Used in the URL: <code>/page/[page-id]</code></li>
            <li>Cannot be changed after creation</li>
          </ul>
        </li>
        <li><strong>Page Title:</strong> 
          <ul>
            <li>Display name of the page</li>
            <li>Shown in page headers and navigation</li>
            <li>Can be changed anytime</li>
          </ul>
        </li>
        <li><strong>Description:</strong> 
          <ul>
            <li>Brief description of the page content</li>
            <li>Used for SEO and page listings</li>
            <li>Optional but recommended</li>
          </ul>
        </li>
        <li><strong>Page Type:</strong> 
          <ul>
            <li>Select from predefined types (General, Article, Tune Notation, Biography, etc.)</li>
            <li>Determines which metadata fields are available</li>
            <li>Can be changed, but metadata will be cleared when changing types</li>
          </ul>
        </li>
      </ul>

      <h4>Type-Specific Metadata</h4>
      <p>Depending on the page type you select, additional metadata fields will appear:</p>
      <ul>
        <li><strong>Tune Notation:</strong> Artist, Tune Type, Key Signature, Time Signature, Region, Tags, etc.</li>
        <li><strong>Article:</strong> Author, Category, Publication Date, Tags, etc.</li>
        <li><strong>Biography:</strong> Person Name, Birth Date, Region, Tags, etc.</li>
        <li><strong>Recording:</strong> Artist, Album, Release Date, Genre, etc.</li>
        <li><strong>General:</strong> No additional metadata fields</li>
      </ul>

      <h4>Saving Metadata</h4>
      <ol>
        <li>Fill in all required fields (Page ID and Title are required)</li>
        <li>Select a page type</li>
        <li>Fill in any type-specific metadata fields</li>
        <li>Click <strong>"Save Page Info"</strong> button</li>
        <li>You'll see a success message, and the page is now created</li>
      </ol>

      <div className="doc-card" style={{ marginTop: '20px', border: '1px solid var(--color-primary)' }}>
        <h4>💡 Auto-Fill Metadata Feature</h4>
        <p>The system includes an <strong>"Auto-Fill Metadata"</strong> button that can automatically generate tags and metadata based on your page content. This is especially useful for:</p>
        <ul>
          <li>Auto-detecting tune types (Reel, Jig, Strathspey, etc.)</li>
          <li>Identifying regions (Cape Breton, Scottish, Irish, etc.)</li>
          <li>Generating relevant tags from content</li>
        </ul>
        <p>Click the button after adding some content to see it in action!</p>
      </div>
    </div>

    <div className="doc-card">
      <h3>Step 2: Editing HTML Content</h3>
      
      <p>After saving the page metadata, you can now edit the HTML content:</p>
      
      <h4>The HTML Editor</h4>
      <ul>
        <li><strong>Rich Text Editor:</strong> Visual editor with formatting toolbar</li>
        <li><strong>HTML View:</strong> Switch to HTML code view for advanced editing</li>
        <li><strong>Dual View:</strong> See both visual and HTML simultaneously</li>
        <li><strong>Auto-Save:</strong> Content is saved automatically as you type</li>
      </ul>

      <h4>Editor Features</h4>
      <ul>
        <li><strong>Formatting:</strong> Bold, italic, headings, lists, links</li>
        <li><strong>Media:</strong> Insert images, audio files, videos</li>
        <li><strong>Product Links:</strong> Use the product picker to link to products</li>
        <li><strong>Templates:</strong> Insert pre-built templates for common page types</li>
        <li><strong>Code Blocks:</strong> Add code snippets with syntax highlighting</li>
      </ul>

      <h4>Adding Content</h4>
      <ol>
        <li>Click in the editor area</li>
        <li>Start typing or paste content</li>
        <li>Use the toolbar to format text</li>
        <li>Insert images, links, or other media as needed</li>
        <li>Content saves automatically</li>
      </ol>

      <h4>Using Templates</h4>
      <p>For common page types (like tune pages), you can insert templates:</p>
      <ol>
        <li>Click the <strong>"Insert Template"</strong> button in the editor</li>
        <li>Select a template type (e.g., "Tune Page Template")</li>
        <li>The template HTML is inserted into the editor</li>
        <li>Customize the template with your content</li>
        <li>Link assets (images, audio) to the template placeholders</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Page Management</h3>
      
      <h4>Viewing All Pages</h4>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Page Management</strong></li>
        <li>You'll see a list of all pages with:
          <ul>
            <li>Page ID and Title</li>
            <li>Page Type</li>
            <li>Last Modified Date</li>
            <li>Actions (Edit, Preview, Delete)</li>
          </ul>
        </li>
      </ol>

      <h4>Searching and Filtering</h4>
      <ul>
        <li><strong>Search:</strong> Type in the search box to find pages by title, ID, or content</li>
        <li><strong>Filter by Type:</strong> Use the dropdown to show only specific page types</li>
        <li><strong>Pagination:</strong> Navigate through pages if you have many</li>
      </ul>

      <h4>Editing Existing Pages</h4>
      <ol>
        <li>Find the page in the list</li>
        <li>Click <strong>"Edit"</strong> button</li>
        <li>You'll be taken to the page editor</li>
        <li>Make changes to metadata or content</li>
        <li>Click <strong>"Save Page Info"</strong> for metadata changes</li>
        <li>Content saves automatically</li>
      </ol>

      <h4>Previewing Pages</h4>
      <ol>
        <li>Click <strong>"Preview"</strong> button on any page</li>
        <li>A modal opens showing how the page will look to visitors</li>
        <li>You can also click the page title to view the live page</li>
      </ol>

      <h4>Deleting Pages</h4>
      <ol>
        <li>Click <strong>"Delete"</strong> button on a page</li>
        <li>Confirm the deletion</li>
        <li><strong>Warning:</strong> This action cannot be undone!</li>
        <li>The page is permanently removed from the system</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Bulk Operations</h3>
      
      <h4>Exporting Pages</h4>
      <ol>
        <li>Select multiple pages using checkboxes</li>
        <li>Click <strong>"Export Selected"</strong> button</li>
        <li>A ZIP file is created containing:
          <ul>
            <li>Page metadata and HTML content</li>
            <li>All associated assets (images, audio, etc.)</li>
            <li>Import instructions</li>
          </ul>
        </li>
        <li>Use this for backups or migrating content</li>
      </ol>

      <h4>Importing Pages</h4>
      <ol>
        <li>Click <strong>"Import ZIP"</strong> button</li>
        <li>Select a ZIP file exported from the system</li>
        <li>Pages and assets are imported automatically</li>
        <li>Existing pages with the same ID will be updated</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Page Types Explained</h3>
      
      <ul>
        <li><strong>General:</strong> Standard content pages (About, Contact, Policies)</li>
        <li><strong>Article:</strong> Blog posts, news articles, educational content</li>
        <li><strong>Tune Notation:</strong> Musical notation pages with ABC code and audio</li>
        <li><strong>Biography:</strong> Biographical pages about musicians or artists</li>
        <li><strong>Recording:</strong> Album or track information pages</li>
        <li><strong>Product Detail:</strong> Detailed product information pages</li>
        <li><strong>Tutorial:</strong> How-to guides and tutorials</li>
        <li><strong>News:</strong> News articles and announcements</li>
      </ul>
      
      <p><strong>Note:</strong> Each page type has specific metadata fields that help with organization and searchability.</p>
    </div>

    <div className="doc-card important">
      <h3>💡 Best Practices</h3>
      <ul>
        <li><strong>Use Descriptive Page IDs:</strong> Make them clear and memorable (e.g., "about-us" not "page1")</li>
        <li><strong>Fill in Metadata:</strong> Complete all metadata fields for better SEO and searchability</li>
        <li><strong>Use Appropriate Page Types:</strong> Select the correct type to get relevant metadata fields</li>
        <li><strong>Add Tags:</strong> Use tags generously to help users find related content</li>
        <li><strong>Link Related Products:</strong> Connect pages to products to increase sales</li>
        <li><strong>Preview Before Publishing:</strong> Always preview pages to ensure they look correct</li>
        <li><strong>Keep Content Updated:</strong> Regularly review and update page content</li>
      </ul>
    </div>

    <div className="doc-card warning">
      <h3>⚠️ Important Notes</h3>
      <ul>
        <li><strong>Page ID Cannot Be Changed:</strong> Choose carefully when creating a page</li>
        <li><strong>Changing Page Type Clears Metadata:</strong> All type-specific metadata is lost when changing types</li>
        <li><strong>Deletions Are Permanent:</strong> There's no undo for page deletion</li>
        <li><strong>Content Auto-Saves:</strong> HTML content saves automatically, but metadata requires clicking "Save Page Info"</li>
        <li><strong>URL Format:</strong> Pages are accessible at <code>/page/[page-id]</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Visual Guide</h3>
      <p>The following screenshots illustrate the page creation process:</p>
      
      <div style={{ marginTop: '20px' }}>
        <h4>1. Admin Dashboard</h4>
        <img 
          src="/documentation-screenshots/01-admin-dashboard.png" 
          alt="Admin Dashboard showing navigation to Page Management"
          style={{ width: '100%', maxWidth: '1200px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '20px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        
        <h4>2. Page Management List</h4>
        <img 
          src="/documentation-screenshots/02-page-management-list.png" 
          alt="Page Management list showing all pages"
          style={{ width: '100%', maxWidth: '1200px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '20px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        
        <h4>3. Create Page Form (Step 1 - Metadata)</h4>
        <img 
          src="/documentation-screenshots/03-create-page-metadata-form.png" 
          alt="Page creation form with metadata fields"
          style={{ width: '100%', maxWidth: '1200px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '20px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        
        <h4>4. Filled Metadata Form</h4>
        <img 
          src="/documentation-screenshots/04-filled-metadata-form.png" 
          alt="Metadata form with all fields filled in"
          style={{ width: '100%', maxWidth: '1200px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '20px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        
        <h4>5. HTML Editor (Step 2)</h4>
        <img 
          src="/documentation-screenshots/05-html-editor-step2.png" 
          alt="HTML editor interface for editing page content"
          style={{ width: '100%', maxWidth: '1200px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '20px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        
        <h4>6. Editor with Content</h4>
        <img 
          src="/documentation-screenshots/06-editor-with-content.png" 
          alt="HTML editor showing page content"
          style={{ width: '100%', maxWidth: '1200px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '20px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        
        <h4>7. Page Management with New Page</h4>
        <img 
          src="/documentation-screenshots/07-page-management-with-new-page.png" 
          alt="Page management list showing the newly created page"
          style={{ width: '100%', maxWidth: '1200px', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '20px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>
      
      <p style={{ marginTop: '20px', fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
        <strong>Note:</strong> Screenshots are captured automatically. If images don't appear, run the screenshot capture script to generate them.
      </p>
    </div>
  </div>
);

// Getting Started Section - Comprehensive guide for new users
const GettingStartedSection = () => (
  <div className="doc-section">
    <h2>🚀 Getting Started Guide</h2>
    
    <div className="doc-card">
      <h3>Welcome to Cranford Publications Admin</h3>
      <p>
        This comprehensive guide will help you get started with managing your Cranford Publications website. 
        Whether you're processing orders, managing products, or configuring settings, this guide covers everything you need to know.
      </p>
    </div>

    <div className="doc-card">
      <h3>📋 Quick Start Checklist</h3>
      <p>Follow these steps to get up and running:</p>
      <ol>
        <li><strong>Login to Admin Panel:</strong> Navigate to <code>/admin</code> and login with your credentials</li>
        <li><strong>Review Dashboard:</strong> Familiarize yourself with the main admin dashboard</li>
        <li><strong>Check Orders:</strong> View pending orders in Order Management</li>
        <li><strong>Review Products:</strong> Verify your product catalog is up to date</li>
        <li><strong>Configure Settings:</strong> Set up shipping rates, tax rules, and email templates</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>🎨 Choosing Your Theme</h3>
      <p>
        The admin panel fully supports multiple colour themes. You can switch themes at any time using the 
        floating theme selector in the bottom-right corner of the screen.
      </p>
      <ul>
        <li><strong>Available themes:</strong> Dark, Light, Ocean, Forest, Sunset, Midnight</li>
        <li>Click the palette icon (<strong>🎨</strong>) to open the theme picker</li>
        <li>Your selection is saved automatically and persists between sessions</li>
        <li>All admin pages — orders, products, pages, assets, settings, and more — adapt to the selected theme</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🎯 Common Daily Tasks</h3>
      <div className="feature-grid">
        <div className="feature-item">
          <h4>📦 Process Orders</h4>
          <p>Check new orders, update status, and send confirmation emails</p>
          <code>Admin → Orders</code>
        </div>
        <div className="feature-item">
          <h4>💳 Verify Payments</h4>
          <p>Cross-reference orders with Stripe and PayPal dashboards</p>
          <code>Admin → Payment Dashboards</code>
        </div>
        <div className="feature-item">
          <h4>📧 Respond to Inquiries</h4>
          <p>Answer customer questions and provide order updates</p>
          <code>Check Email</code>
        </div>
        <div className="feature-item">
          <h4>📊 Review Analytics</h4>
          <p>Monitor sales, traffic, and product performance</p>
          <code>Google Analytics</code>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h3>🔐 Security Best Practices</h3>
      <ul>
        <li><strong>Use Strong Passwords:</strong> Use a password manager and enable 2FA where possible</li>
        <li><strong>Logout When Done:</strong> Always logout after completing admin tasks</li>
        <li><strong>Verify Before Refunds:</strong> Confirm customer identity before processing refunds</li>
        <li><strong>Backup Regularly:</strong> Database backups run automatically, but verify they're working</li>
        <li><strong>Monitor Activity:</strong> Check for unusual orders or suspicious activity</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>📱 Accessing the Admin Panel</h3>
      <p><strong>Desktop:</strong></p>
      <ol>
        <li>Navigate to <code>https://cranfordpub.ca/admin</code></li>
        <li>Enter your username and password</li>
        <li>You'll be redirected to the Admin Dashboard</li>
      </ol>
      <p><strong>Mobile:</strong></p>
      <ol>
        <li>The admin panel is mobile-responsive</li>
        <li>Access the same URL on your mobile device</li>
        <li>Navigation adapts to smaller screens</li>
      </ol>
    </div>

    <div className="doc-card important">
      <h3>⚠️ Important Notes</h3>
      <ul>
        <li><strong>Test Orders:</strong> Use Stripe test mode for testing - never use real cards in test mode</li>
        <li><strong>Email Verification:</strong> Always verify customer email addresses before shipping</li>
        <li><strong>Shipping Addresses:</strong> Double-check addresses for accuracy before printing labels</li>
        <li><strong>Refund Policy:</strong> Follow the documented refund policy consistently</li>
        <li><strong>Customer Communication:</strong> Respond to inquiries within 24 hours when possible</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🆘 Getting Help</h3>
      <p>If you need assistance:</p>
      <ul>
        <li><strong>Documentation:</strong> Refer to the specific section guides in this documentation</li>
        <li><strong>Technical Support:</strong> Contact the developer at Markus.van.Kempen@gmail.com</li>
        <li><strong>System Issues:</strong> Report bugs using the Bug Report feature in the admin panel</li>
        <li><strong>Questions:</strong> Review the FAQ section first, then reach out if needed</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>📚 Next Steps</h3>
      <p>Once you're familiar with the basics, explore these topics:</p>
      <ol>
        <li><strong>Order Processing:</strong> Learn the complete order fulfillment workflow</li>
        <li><strong>Product Management:</strong> Add, edit, and organize your product catalog</li>
        <li><strong>Customer Management:</strong> View customer accounts and order history</li>
        <li><strong>Content Management:</strong> Create articles, tune pages, and custom pages</li>
        <li><strong>Configuration:</strong> Set up shipping rates, tax rules, and email templates</li>
      </ol>
    </div>
  </div>
);

// Customer Management Section
const CustomerManagementSection = () => (
  <div className="doc-section">
    <h2>👥 Customer Management</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>
        The customer management system allows you to view customer accounts, order history, 
        and manage customer-related issues. This section covers all aspects of customer administration.
      </p>
    </div>

    <div className="doc-card">
      <h3>📝 Viewing Customer Accounts</h3>
      <p><strong>Step-by-Step Guide:</strong></p>
      <ol>
        <li>Navigate to <strong>Admin → Order Management</strong></li>
        <li>Click on any order to view customer details</li>
        <li>Customer information includes:
          <ul>
            <li>Name and email address</li>
            <li>Shipping address</li>
            <li>Order history</li>
            <li>Total amount spent</li>
            <li>Account creation date</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>🔍 Finding Customer Information</h3>
      <p><strong>Search Methods:</strong></p>
      <ul>
        <li><strong>By Email:</strong> Use the order search to find all orders by email address</li>
        <li><strong>By Name:</strong> Search orders by customer name</li>
        <li><strong>By Order Number:</strong> Direct lookup using order ID</li>
        <li><strong>By Date Range:</strong> Filter orders by date to find specific transactions</li>
      </ul>
      
      <div className="code-block">
Example: Finding all orders from john@example.com
1. Go to Admin → Orders
2. Use the search box: john@example.com
3. View all orders and total spend
      </div>
    </div>

    <div className="doc-card">
      <h3>📧 Customer Communication</h3>
      <p><strong>Best Practices:</strong></p>
      <ul>
        <li><strong>Response Time:</strong> Aim to respond within 24 hours</li>
        <li><strong>Professional Tone:</strong> Maintain friendly, professional communication</li>
        <li><strong>Order Updates:</strong> Proactively inform customers of shipping delays</li>
        <li><strong>Problem Resolution:</strong> Address issues promptly and fairly</li>
      </ul>
      
      <p><strong>Common Customer Inquiries:</strong></p>
      <ol>
        <li><strong>"Where is my order?"</strong>
          <ul>
            <li>Check order status in Order Management</li>
            <li>Provide tracking number if available</li>
            <li>Estimate delivery based on shipping method</li>
          </ul>
        </li>
        <li><strong>"Can I change my order?"</strong>
          <ul>
            <li>If unfulfilled: Modify order details directly</li>
            <li>If shipped: Cannot change, offer alternative solutions</li>
          </ul>
        </li>
        <li><strong>"I need a refund"</strong>
          <ul>
            <li>Review refund policy</li>
            <li>Verify order and issue details</li>
            <li>Process refund through appropriate section</li>
          </ul>
        </li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>🔒 Customer Privacy</h3>
      <p><strong>Data Protection:</strong></p>
      <ul>
        <li><strong>Confidentiality:</strong> Never share customer information with third parties</li>
        <li><strong>Secure Communication:</strong> Use encrypted channels for sensitive information</li>
        <li><strong>Data Retention:</strong> Follow legal requirements for data storage</li>
        <li><strong>Access Control:</strong> Only authorized users should access customer data</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>📊 Customer Analytics</h3>
      <p>Track customer behavior and patterns:</p>
      <ul>
        <li><strong>Repeat Customers:</strong> Identify customers who order frequently</li>
        <li><strong>High-Value Customers:</strong> Track customers with large order totals</li>
        <li><strong>Geographic Distribution:</strong> Analyze where customers are located</li>
        <li><strong>Product Preferences:</strong> See which products are most popular</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>⚠️ Handling Difficult Situations</h3>
      <p><strong>Problem Resolution Guide:</strong></p>
      <ol>
        <li><strong>Listen:</strong> Understand the customer's concern fully</li>
        <li><strong>Empathize:</strong> Acknowledge their frustration</li>
        <li><strong>Investigate:</strong> Review order details and transaction history</li>
        <li><strong>Solution:</strong> Offer appropriate resolution (refund, replacement, credit)</li>
        <li><strong>Follow-up:</strong> Ensure customer is satisfied with resolution</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>✉️ Email Templates</h3>
      <p>Use these templates for common scenarios:</p>
      
      <h4>Order Confirmation</h4>
      <div className="code-block">
Subject: Order Confirmation - Order #[ORDER_ID]

Dear [CUSTOMER_NAME],

Thank you for your order! We're pleased to confirm your purchase:

Order Number: [ORDER_ID]
Order Total: $[TOTAL] CAD
Estimated Ship Date: [DATE]

Your order will be carefully prepared and shipped to:
[SHIPPING_ADDRESS]

If you have any questions, please don't hesitate to contact us.

Best regards,
Cranford Publications
      </div>

      <h4>Shipping Notification</h4>
      <div className="code-block">
Subject: Your Order Has Shipped - Order #[ORDER_ID]

Dear [CUSTOMER_NAME],

Great news! Your order has been shipped.

Tracking Number: [TRACKING]
Estimated Delivery: [DATE]

You can track your package at [CARRIER_LINK]

Thank you for your purchase!

Best regards,
Cranford Publications
      </div>
    </div>
  </div>
);

// Inventory Management Section
const InventoryManagementSection = () => (
  <div className="doc-section">
    <h2>📊 Inventory Management</h2>
    
    <div className="doc-card">
      <h3>Overview</h3>
      <p>
        Effective inventory management ensures you never oversell products and can fulfill orders promptly. 
        This guide covers tracking stock levels, managing product availability, and handling inventory updates.
      </p>
    </div>

    <div className="doc-card">
      <h3>📦 Tracking Stock Levels</h3>
      <p><strong>Monitoring Inventory:</strong></p>
      <ol>
        <li>Navigate to <strong>Admin → Product Management</strong></li>
        <li>Review the product list showing current stock levels</li>
        <li>Look for products with low stock (indicated by warning colors)</li>
        <li>Update quantities as needed</li>
      </ol>
      
      <p><strong>Stock Level Indicators:</strong></p>
      <ul>
        <li><strong>In Stock:</strong> Green indicator, sufficient quantity available</li>
        <li><strong>Low Stock:</strong> Yellow indicator, consider reordering</li>
        <li><strong>Out of Stock:</strong> Red indicator, product unavailable</li>
        <li><strong>Unlimited:</strong> Digital products or items not tracked</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🔄 Updating Inventory</h3>
      <p><strong>Manual Updates:</strong></p>
      <ol>
        <li>Go to <strong>Admin → Product Management</strong></li>
        <li>Click <strong>Edit</strong> on the product you want to update</li>
        <li>Locate the <strong>Stock Quantity</strong> field</li>
        <li>Enter the new quantity</li>
        <li>Click <strong>Save</strong> to update</li>
      </ol>
      
      <div className="code-block">
Example: Received new shipment of 50 CDs
1. Find product "Celtic Fiddle Vol. 1 CD"
2. Current stock: 12
3. Add 50 to inventory
4. New stock: 62
5. Save changes
      </div>
    </div>

    <div className="doc-card">
      <h3>📋 Inventory Best Practices</h3>
      <ul>
        <li><strong>Regular Audits:</strong> Physically count inventory monthly to verify accuracy</li>
        <li><strong>Reorder Points:</strong> Set minimum stock levels for automatic reorder alerts</li>
        <li><strong>First In, First Out (FIFO):</strong> Ship oldest stock first to prevent obsolescence</li>
        <li><strong>Damaged Items:</strong> Remove damaged inventory from stock counts</li>
        <li><strong>Seasonal Planning:</strong> Increase stock before peak seasons</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🎵 Digital vs Physical Products</h3>
      <p><strong>Physical Products (Books, CDs):</strong></p>
      <ul>
        <li>Track quantity on hand</li>
        <li>Decrease stock when orders ship</li>
        <li>Set low stock alerts</li>
        <li>Plan for reorder time</li>
      </ul>
      
      <p><strong>Digital Products (MP3s, PDFs):</strong></p>
      <ul>
        <li>Set as "unlimited" stock</li>
        <li>No inventory tracking needed</li>
        <li>Instant delivery available</li>
        <li>No storage costs</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>⚠️ Handling Out-of-Stock Situations</h3>
      <p><strong>When a Product Runs Out:</strong></p>
      <ol>
        <li><strong>Mark as Out of Stock:</strong> Set quantity to 0 in admin</li>
        <li><strong>Hide from Store:</strong> Option to hide product from public view</li>
        <li><strong>Notify Customers:</strong> Email customers on backorder list</li>
        <li><strong>Estimate Restock Date:</strong> Provide expected availability date</li>
        <li><strong>Offer Alternatives:</strong> Suggest similar products</li>
      </ol>
      
      <div className="code-block">
Example Email to Customer:
"We apologize, but [PRODUCT NAME] is currently out of stock.
We expect to receive more inventory by [DATE].
Would you like us to notify you when it's available?"
      </div>
    </div>

    <div className="doc-card">
      <h3>📊 Inventory Reports</h3>
      <p><strong>Generate Reports For:</strong></p>
      <ul>
        <li><strong>Current Stock Levels:</strong> Snapshot of all product quantities</li>
        <li><strong>Low Stock Alert:</strong> Products below reorder point</li>
        <li><strong>Out of Stock:</strong> Products currently unavailable</li>
        <li><strong>Sales Velocity:</strong> How quickly products sell</li>
        <li><strong>Inventory Value:</strong> Total value of stock on hand</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>🔢 Stock Adjustment Reasons</h3>
      <p>Common reasons to adjust inventory:</p>
      <ul>
        <li><strong>Received Shipment:</strong> Add new stock from supplier</li>
        <li><strong>Physical Count:</strong> Correct discrepancies found during audit</li>
        <li><strong>Damaged Goods:</strong> Remove unsellable items</li>
        <li><strong>Returns:</strong> Add returned items back to stock</li>
        <li><strong>Samples/Gifts:</strong> Deduct items used for promotional purposes</li>
        <li><strong>Theft/Loss:</strong> Adjust for missing inventory</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>🎯 Preventing Overselling</h3>
      <p><strong>Critical Steps:</strong></p>
      <ul>
        <li><strong>Real-Time Updates:</strong> Stock decreases immediately when order is placed</li>
        <li><strong>Reserve Inventory:</strong> Items in unpaid carts are temporarily reserved</li>
        <li><strong>Sync Across Channels:</strong> If selling on multiple platforms, sync inventory</li>
        <li><strong>Safety Buffer:</strong> Consider keeping 1-2 units as safety stock</li>
      </ul>
      
      <p><strong>If Overselling Occurs:</strong></p>
      <ol>
        <li>Contact customer immediately</li>
        <li>Offer alternatives or refund</li>
        <li>Provide discount on future order as apology</li>
        <li>Review why overselling happened and prevent recurrence</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>📦 Reordering Workflow</h3>
      <p><strong>Step-by-Step Process:</strong></p>
      <ol>
        <li><strong>Check Stock Levels:</strong> Review inventory report</li>
        <li><strong>Calculate Needs:</strong> Based on sales velocity and lead time</li>
        <li><strong>Contact Supplier:</strong> Place order for required quantities</li>
        <li><strong>Track Shipment:</strong> Monitor delivery progress</li>
        <li><strong>Receive & Verify:</strong> Count items and check quality</li>
        <li><strong>Update System:</strong> Add new stock to inventory</li>
        <li><strong>Update Customers:</strong> Notify those on backorder list</li>
      </ol>
    </div>
  </div>
);

const ArticleCreationSection = () => (
  <div className="doc-section">
    <h2>✍️ How to Create an Article</h2>
    
    <div className="doc-card">
      <p><strong>Last Updated:</strong> November 12, 2025</p>
      <p>This comprehensive guide will walk you through the process of creating a new article on the Cranford Publications website, complete with step-by-step instructions and screenshots.</p>
    </div>

    <div className="doc-card">
      <h3>Prerequisites</h3>
      <p>Before you begin, ensure you have:</p>
      <ul>
        <li>✅ Admin or Content Creator account credentials</li>
        <li>✅ Access to the admin dashboard</li>
        <li>✅ Basic knowledge of HTML (for content formatting)</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Step 1: Logging In</h3>
      <ol>
        <li>Navigate to the login page at <code>http://localhost:3000/login</code> (or your production URL)</li>
        <li>Enter your <strong>Username or Email</strong> and <strong>Password</strong></li>
        <li>Click the <strong>Login</strong> button</li>
      </ol>
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/01-login-page.png" 
          alt="Login Page" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 1: Login page where you enter your credentials</em>
        </p>
      </div>
    </div>

    <div className="doc-card">
      <h3>Step 2: Accessing the Admin Dashboard</h3>
      <p>After logging in, you'll be redirected to the homepage. To access the admin area:</p>
      <ol>
        <li>Click on your <strong>Account</strong> icon in the top right corner</li>
        <li>Select <strong>⚙️ Admin Dashboard</strong> from the dropdown menu</li>
      </ol>
      <p>Alternatively, you can navigate directly to <code>/admin</code> in your browser.</p>
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/02-admin-dashboard.png" 
          alt="Admin Dashboard" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 2: Admin Dashboard showing all available management sections</em>
        </p>
      </div>
      <p>The Admin Dashboard provides access to various management sections:</p>
      <ul>
        <li>Site Settings</li>
        <li>Product Management</li>
        <li><strong>Page Management</strong> ← This is what we need!</li>
        <li>Asset Management</li>
        <li>User Management</li>
        <li>And more...</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Step 3: Navigating to Page Management</h3>
      <ol>
        <li>From the Admin Dashboard, locate the <strong>📄 Page Management</strong> card</li>
        <li>Click on it to open the Page Management interface</li>
      </ol>
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/03-page-management.png" 
          alt="Page Management" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 3: Page Management interface showing existing pages</em>
        </p>
      </div>
      <p>The Page Management page displays:</p>
      <ul>
        <li>A list of all existing pages</li>
        <li>Search and filter options</li>
        <li>The <strong>"+ Create New Page"</strong> button (top right)</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Step 4: Creating a New Page</h3>
      <ol>
        <li>Click the <strong>"+ Create New Page"</strong> button in the top right corner</li>
        <li>A modal will appear with a form to create a new page</li>
      </ol>
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/04-create-page-modal.png" 
          alt="Create Page Modal" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 4: Create New Page modal form</em>
        </p>
      </div>
    </div>

    <div className="doc-card">
      <h3>Step 5: Filling in Page Information</h3>
      <p>Fill out the form with the following information:</p>
      
      <h4>Required Fields:</h4>
      
      <p><strong>1. Page ID</strong> (Required)</p>
      <ul>
        <li>Use lowercase letters, numbers, and hyphens only</li>
        <li>Example: <code>my-article-title</code></li>
        <li>This will be used in the URL: <code>/page/my-article-title</code></li>
      </ul>

      <p><strong>2. Page Title</strong> (Required)</p>
      <ul>
        <li>The display title of your article</li>
        <li>Example: <code>My Article Title</code></li>
      </ul>

      <p><strong>3. Description</strong> (Optional)</p>
      <ul>
        <li>A brief description of the article content</li>
        <li>This helps with SEO and organization</li>
      </ul>

      <p><strong>4. Page Type</strong> (Required)</p>
      <ul>
        <li>Select <strong>"article"</strong> from the dropdown or type it in</li>
        <li>⚠️ <strong>Important:</strong> Use lowercase letters only (e.g., "article", not "Article")</li>
        <li>Other page types include: "tune-notation", "product-detail", "biography", etc.</li>
      </ul>

      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/05-filled-create-form.png" 
          alt="Filled Create Form" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 5: Create page form filled with sample data</em>
        </p>
      </div>

      <p>5. Click the <strong>"Create Page"</strong> button</p>
    </div>

    <div className="doc-card">
      <h3>Step 6: Adding Article Content</h3>
      <p>After clicking "Create Page", you'll be redirected to the Page Editor.</p>
      
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/06-page-editor.png" 
          alt="Page Editor" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 6: Page Editor interface with metadata fields</em>
        </p>
      </div>

      <h4>Page Information Section</h4>
      <p>You can update the page information here:</p>
      <ul>
        <li><strong>Page Title:</strong> Edit the title if needed</li>
        <li><strong>Description:</strong> Update the description</li>
        <li><strong>Page Type:</strong> Verify it's set to "Article"</li>
      </ul>

      <h4>Article Metadata Section</h4>
      <p>Fill in the article-specific metadata:</p>
      <ul>
        <li><strong>Author:</strong> Enter the author's name (e.g., "Paul Stewart Cranford")</li>
        <li><strong>Publication Date:</strong> Enter the original publication date (format: yyyy-mm-dd)</li>
        <li><strong>Category:</strong> Select or enter a category (e.g., "History", "Biography", "Cultural")</li>
        <li><strong>Source/Publication:</strong> Where the article was originally published</li>
        <li><strong>Product ID:</strong> Link to a product if applicable</li>
        <li><strong>Tags:</strong> Comma-separated tags (e.g., "cape breton, history, fiddlers")
          <ul>
            <li>💡 <strong>Tip:</strong> Use the "✨ Auto-Fill Metadata" button to generate tags from content</li>
          </ul>
        </li>
      </ul>

      <h4>Adding Content</h4>
      <ol>
        <li>Scroll down to the <strong>"Page Content"</strong> section</li>
        <li>Click the <strong>"✏️ HTML Editor"</strong> button</li>
      </ol>

      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/07-html-editor.png" 
          alt="HTML Editor" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 7: HTML Editor interface with Simple and Rich Editor options</em>
        </p>
      </div>

      <p>The editor provides two options:</p>
      <ul>
        <li><strong>Simple Editor:</strong> Edit raw HTML with live preview</li>
        <li><strong>Rich Editor (Tiptap):</strong> WYSIWYG editor for advanced formatting</li>
      </ul>

      <p>3. Type or paste your article content in HTML format</p>
      
      <p><strong>Example HTML content:</strong></p>
      <pre className="code-block">{`<h1>Welcome to My Article</h1>
<p>This is a sample article created using the page editor. You can add content here using HTML.</p>
<h2>Section 1: Introduction</h2>
<p>This is the introduction section of the article. You can format text using HTML tags.</p>
<h2>Section 2: Main Content</h2>
<p>Here you can add the main content of your article. Use HTML tags to format your text, add links, images, and more.</p>
<ul>
<li>First bullet point</li>
<li>Second bullet point</li>
<li>Third bullet point</li>
</ul>`}</pre>

      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/08-editor-with-content.png" 
          alt="Editor with Content" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 8: HTML Editor showing content in the left panel and live preview in the right panel</em>
        </p>
      </div>

      <p>The editor features:</p>
      <ul>
        <li><strong>Left Panel:</strong> HTML code editor</li>
        <li><strong>Right Panel:</strong> Live preview of how the content will appear</li>
        <li><strong>Toolbar:</strong> Buttons to insert links, styles, assets, and templates</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Step 7: Saving Your Article</h3>
      <ol>
        <li>Review your content in the live preview panel</li>
        <li>Click the <strong>"💾 Save Changes"</strong> button at the top right of the editor</li>
        <li>You'll see a success message: "✅ Content saved successfully! (Version 1)"</li>
      </ol>
      
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <img 
          src="/tutorial-images/09-saved-successfully.png" 
          alt="Saved Successfully" 
          style={{ maxWidth: '100%', border: '1px solid var(--color-border)', borderRadius: '8px' }}
        />
        <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
          <em>Figure 9: Success message after saving the article</em>
        </p>
      </div>

      <p>4. You can continue editing or click <strong>"❓ Back to Pages"</strong> to return to the page list</p>
    </div>

    <div className="doc-card important">
      <h3>💡 Tips and Best Practices</h3>
      
      <h4>Content Formatting</h4>
      <ul>
        <li><strong>Use semantic HTML:</strong> Use proper heading tags (<code>&lt;h1&gt;</code>, <code>&lt;h2&gt;</code>, etc.) for structure</li>
        <li><strong>Paragraphs:</strong> Wrap text in <code>&lt;p&gt;</code> tags</li>
        <li><strong>Lists:</strong> Use <code>&lt;ul&gt;</code> for bullet lists, <code>&lt;ol&gt;</code> for numbered lists</li>
        <li><strong>Links:</strong> Use <code>&lt;a href="url"&gt;link text&lt;/a&gt;</code> for hyperlinks</li>
        <li><strong>Images:</strong> Use the "🖼️ Insert Asset" button to add images</li>
      </ul>

      <h4>SEO Best Practices</h4>
      <ul>
        <li><strong>Descriptive Page ID:</strong> Use clear, descriptive page IDs</li>
        <li><strong>Complete Metadata:</strong> Fill in all metadata fields for better SEO</li>
        <li><strong>Tags:</strong> Add relevant tags to help with categorization and search</li>
        <li><strong>Descriptions:</strong> Write clear, concise descriptions</li>
      </ul>

      <h4>Article Organization</h4>
      <ul>
        <li><strong>Categories:</strong> Use consistent categories across similar articles</li>
        <li><strong>Tags:</strong> Use tags to group related articles</li>
        <li><strong>Authors:</strong> Always include author information</li>
        <li><strong>Publication Dates:</strong> Include original publication dates when available</li>
      </ul>

      <h4>Editor Tips</h4>
      <ul>
        <li><strong>Auto-Save:</strong> The editor has auto-save functionality</li>
        <li><strong>Version History:</strong> Each save creates a new version</li>
        <li><strong>Preview:</strong> Always preview your content before publishing</li>
        <li><strong>Switch Editors:</strong> You can switch between Simple and Rich editors anytime</li>
      </ul>
    </div>

    <div className="doc-card warning">
      <h3>⚠️ Troubleshooting</h3>
      
      <h4>Common Issues</h4>
      
      <p><strong>Issue: "Page type must be lowercase letters, numbers, and hyphens only"</strong></p>
      <p><strong>Solution:</strong> Make sure you're using lowercase for the page type (e.g., "article" not "Article")</p>

      <p><strong>Issue: Page not saving</strong></p>
      <p><strong>Solution:</strong></p>
      <ul>
        <li>Check your internet connection</li>
        <li>Ensure all required fields are filled</li>
        <li>Try refreshing the page and saving again</li>
      </ul>

      <p><strong>Issue: Content not displaying correctly</strong></p>
      <p><strong>Solution:</strong></p>
      <ul>
        <li>Check your HTML syntax</li>
        <li>Use the live preview to see how content will render</li>
        <li>Ensure all HTML tags are properly closed</li>
      </ul>

      <p><strong>Issue: Can't find the Create New Page button</strong></p>
      <p><strong>Solution:</strong></p>
      <ul>
        <li>Ensure you're logged in as an admin or content creator</li>
        <li>Check that you're on the <code>/admin/pages</code> page</li>
        <li>Refresh the page if needed</li>
      </ul>

      <h4>Getting Help</h4>
      <p>If you encounter issues not covered here:</p>
      <ol>
        <li>Check the <strong>📚 Documentation</strong> section in the admin dashboard</li>
        <li>Contact the system administrator</li>
        <li>Review the technical documentation for advanced features</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Next Steps</h3>
      <p>After creating your article:</p>
      <ol>
        <li><strong>Preview:</strong> Use the "👁️ Preview" button to see how it will appear to visitors</li>
        <li><strong>Edit:</strong> Return to the editor anytime to make changes</li>
        <li><strong>Publish:</strong> Ensure the page visibility settings allow it to be viewed</li>
        <li><strong>Share:</strong> The article will be accessible at <code>/page/your-page-id</code></li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Summary</h3>
      <p>Creating an article involves these key steps:</p>
      <ol>
        <li>✅ Log in to the admin dashboard</li>
        <li>✅ Navigate to Page Management</li>
        <li>✅ Click "Create New Page"</li>
        <li>✅ Fill in Page ID, Title, Description, and Type (article)</li>
        <li>✅ Add article metadata (author, date, tags, etc.)</li>
        <li>✅ Write your content in the HTML editor</li>
        <li>✅ Save your changes</li>
        <li>✅ Preview and publish</li>
      </ol>
      <p style={{ marginTop: '16px', fontSize: '1.1em', fontWeight: 'bold' }}>
        🎉 <strong>Congratulations!</strong> You've successfully created an article.
      </p>
    </div>

    <div className="doc-card" style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
      <p><em>This guide was created using automated screenshots captured on November 12, 2025. The interface may vary slightly, but the core functionality remains the same.</em></p>
    </div>
  </div>
);

// Version History Section
const VersionHistorySection = () => (
  <div className="doc-section">
    <h2>📅 Version History</h2>

    <div className="doc-card important">
      <h3>About Versioning</h3>
      <p>
        Cranford Publications follows <strong>semantic versioning</strong> (<code>MAJOR.MINOR.PATCH</code>).
        Each release ships a versioned deploy script (<code>Docker/deploy-X.Y.Z.sh</code>),
        updated PWA cache name, and release notes in <code>NewDocumentation/06-Release-Notes/</code>.
        The live API endpoint <code>GET /api/version</code> always reflects the running version.
      </p>
    </div>

    <div className="doc-card">
      <h3>v2.32.1 — September 20, 2026</h3>
      <ul>
        <li><strong>Order reminders:</strong> judge sandbox/live from the payment that was used (do not drop live Stripe orders because PayPal is sandbox)</li>
        <li><strong>Email my cart:</strong> Archive inquiries; weekly digest excludes archived</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.32.1.sh</code> — code only, no database sync</li>
      </ul>
      <h3>v2.32.0 — August 30, 2026</h3>
      <ul>
        <li><strong>Email my cart:</strong> Admin → Orders lists inquiries — View, Reply, Mail, Archive, Delete</li>
        <li><strong>Catalog:</strong> product weights backfilled; 642 name; 645 Books / 0.35 kg</li>
        <li><strong>Deploy:</strong> <code>SYNC_DB=1 Docker/deploy-2.32.0.sh</code> — code + database</li>
      </ul>
      <h3>v2.31.1 — August 23, 2026</h3>
      <ul>
        <li><strong>Page load:</strong> fix cart shipping loop that froze the homepage when a cart was saved</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.31.1.sh</code> — code only</li>
      </ul>
      <h3>v2.31.0 — August 22, 2026</h3>
      <ul>
        <li><strong>Email verification:</strong> resend/register/verify bug fixes; clearer UX; no DB migration</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.31.0.sh</code> — code only</li>
      </ul>
      <h3>v2.30.0 — August 10, 2026</h3>
      <ul>
        <li><strong>SEO:</strong> Path-correct canonicals on <code>/page/*</code>; sitemap excludes API thumbnails and thin index pages; noindex for tune-asset, query-param, and utility URLs; non-www → www; archive mirror <code>old.cranfordpub.ca</code> noindex</li>
        <li><strong>Mail orders:</strong> Live vs Test toggle; test orders <code>FID-MAIL-TEST-…</code>, invoice emails prefixed <strong>[TEST]</strong></li>
        <li><strong>Order reminders:</strong> Exclude test mail orders and Stripe/PayPal sandbox checkout</li>
        <li><strong>Database:</strong> <code>orders.mail_order_mode</code> auto-migrated on server start</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.30.0.sh</code> — code only</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.29.0 — August 5, 2026</h3>
      <ul>
        <li><strong>Mail / check orders:</strong> Same form from Admin Orders or Checkout shipping step; customer search; product search; Customer price vs 🏢 Wholesale pricing; manual discount when no wholesale on product</li>
        <li><strong>Admin shop:</strong> Admins use <code>/shop</code> (full catalog); mail order from checkout without Stripe/PayPal</li>
        <li><strong>Dashboard:</strong> Order Management section moved to top</li>
        <li><strong>Orders table:</strong> Wider layout; Actions column visible on large screens</li>
        <li><strong>API:</strong> <code>GET /api/orders/mail-order/customers/search</code>; mail-order wholesale from product settings (no DB migration)</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.29.0.sh</code> — code only</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.28.0 — August 4, 2026</h3>
      <ul>
        <li><strong>Mail / check orders:</strong> <code>✉️ New mail order</code> — retail, wholesale tier, or custom pricing; invoice email; mark payment received</li>
        <li><strong>Wholesale pricing:</strong> Shared frontend/backend utilities; tier discounts on mail orders</li>
        <li><strong>Payment fees:</strong> <code>Proc. fee</code> column; <code>Export → Processing fees (tax)</code> CSV from Stripe/PayPal APIs</li>
        <li><strong>Checkout:</strong> Transaction fee % persisted on orders; cart/checkout totals include fee when configured</li>
        <li><strong>Documentation:</strong> Mail order guide, payment fees tax export guide, Order Processing section updated</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.28.0.sh</code> — code only (no DB migration)</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.13.0 — June 10, 2026</h3>
      <ul>
        <li><strong>Full admin theming:</strong> All admin pages now fully respect the selected colour theme (no more hardcoded dark palette)</li>
        <li><strong>Header redesign:</strong> Quick-search removed from masthead; Contact/Search/Cart grouped right; duplicate nav entries removed</li>
        <li><strong>Page editor:</strong> Related Content Sections preview respects selected theme</li>
        <li><strong>Article pages:</strong> Metadata box uses theme CSS in light mode</li>
        <li><strong>Tune editor:</strong> Clearing MP3 audio saves correctly; legacy <code>mp3_asset_ids</code> synced on save</li>
        <li><strong>Time signatures:</strong> Restricted to 2/4, 3/4, 4/4, 6/8, 9/8 in admin and import paths</li>
        <li><strong>Documentation:</strong> End-user documentation updated with theme switcher guide, Order Reminders section, and corrected tune creation steps</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.13.0.sh</code> — code-only by default</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.12.0 — May 8, 2026</h3>
      <ul>
        <li><strong>Tune index page linking:</strong> Products can now reference a <code>book-index</code> or <code>cd-index</code> content page via a new <strong>Tune index page</strong> field in the product editor</li>
        <li><strong>TuneIndexPickerModal:</strong> Searchable browse dialog for selecting index pages</li>
        <li><strong>API:</strong> <code>POST/PUT /api/products</code> accepts and persists <code>index_page_id</code></li>
        <li><strong>Storefront:</strong> Product detail honours explicit index page before SKU/title heuristics</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.12.0.sh</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.11.0 — May 6, 2026</h3>
      <ul>
        <li><strong>Orphan detection improvements:</strong> Admin → Assets → Duplicates now scans a much broader set of reference sources (tune staging, newsletters, promotions, templates, etc.)</li>
        <li><strong>Duplicates dashboard:</strong> Shows a reference scan table so operators can verify what counted as "in use"</li>
        <li><strong>API:</strong> <code>GET /api/assets/duplicates/check</code> includes full <code>referenceScan</code> metadata</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.11.0.sh</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.10.0 — May 5, 2026</h3>
      <ul>
        <li><strong>Back button:</strong> Unified across product, article, and tune detail pages — arrow-only style, always uses browser history (<code>navigate(-1)</code>)</li>
        <li><strong>Search/filter state persistence:</strong> Navigating back to <code>/tunes</code>, <code>/articles</code>, and <code>/shop</code> restores previous search, filters, and page via <code>sessionStorage</code></li>
        <li><strong>Admin-only tune nav links:</strong> "Tune Archive", "CB Fiddle", "Original Page" links hidden from regular visitors</li>
        <li><strong>Tune editor fields:</strong> Added TTA URL and CBFiddle URL fields</li>
        <li><strong>Audio cleanup:</strong> Fixed duplicate MP3 asset IDs on 98 tunes (Simon Fraser, Beaton family, Jerry Holland)</li>
        <li><strong>Source URLs:</strong> All 687 tune <code>source_url</code> values migrated from <code>cranfordpub.com</code> → <code>old.cranfordpub.ca</code></li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.10.0.sh</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.9.0 — April 6, 2026</h3>
      <ul>
        <li><strong>PWA:</strong> Service worker cache name <code>cranford-pub-pwa-v2.9.0-mp3</code></li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.9.0.sh</code> with <code>SKIP_RSYNC</code> and <code>SYNC_DB</code> options</li>
        <li><strong>Version hygiene:</strong> All version strings aligned across frontend, backend, manifests, shell HTML</li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.8.0 — April 5, 2026</h3>
      <ul>
        <li><strong>Version hygiene:</strong> CRA shell <code>index.html</code> and backend static shell brought into alignment with <code>package.json</code> version</li>
        <li><strong>PWA:</strong> Cache name bumped; clients pick up new service worker after hard refresh</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.8.0.sh</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.7.0 — April 5, 2026</h3>
      <ul>
        <li><strong>Book index pages:</strong> Tune type labels (jig, reel, strathspey, etc.) aligned with legacy Cranford book indexes via <code>metadata.book_index_types</code></li>
        <li><strong>Book index display:</strong> Tune titles cleaned in browser — long hyphen runs and trailing type text removed</li>
        <li><strong>Header badge:</strong> Renamed from <code>beta-flag</code> to <code>header-version-badge</code> with stable slate style</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.7.0.sh</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.6.0-BETA — March 27, 2026</h3>
      <ul>
        <li><strong>PWA manifest:</strong> Full <code>name</code>, <code>lang</code>, <code>dir</code>, install-friendly labels, and <code>version</code> field</li>
        <li><strong>SEO:</strong> <code>index.html</code> meta <code>version</code> / <code>updated</code>; <code>SEO.js</code> emits matching <code>og:locale</code></li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.6.0.sh</code>; <code>docker-compose-traefik.yml</code> uses <code>cranfordpubweb:latest</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>v2.5.0-BETA — March 27, 2026</h3>
      <ul>
        <li><strong>Page Templates admin:</strong> Tabbed UI — Layouts, transactional email, bulk apply for tune pages, bulk apply for article pages</li>
        <li><strong>Email templates in DB:</strong> Password reset, email verification, registration notice, newsletter welcome, bug report, back-in-stock — seeded and admin-editable</li>
        <li><strong>API:</strong> <code>GET /api/content/templates/email-catalog</code> lists all automated email templates</li>
        <li><strong>Deploy:</strong> <code>Docker/deploy-2.5.0.sh</code></li>
      </ul>
    </div>

    <div className="doc-card">
      <h3>Deployment Notes (All Versions)</h3>
      <ul>
        <li>Every release: version bumped in <code>frontend/package.json</code>, <code>backend/package.json</code>, manifests, shell HTML, and header badge</li>
        <li>PWA cache name always bumped so installed apps pick up the new service worker</li>
        <li>Deploy scripts live at <code>Docker/deploy-X.Y.Z.sh</code> — rsync to VPS, remote Docker build, rollout</li>
        <li>Full release checklist: <code>NewDocumentation/07-Deployment-Guide/STANDARD_RELEASE_PROCEDURE.md</code></li>
      </ul>
    </div>
  </div>
);

// Order Reminders Section
const OrderRemindersSection = () => (
  <div className="doc-section">
    <h2>📨 Order Reminder Emails</h2>

    <div className="doc-card">
      <h3>Overview</h3>
      <p>
        The Order Reminder system sends scheduled <strong>admin digest emails</strong> listing open orders that still need
        processing (status <strong>pending</strong> or <strong>processing</strong>). Use it so nothing sits unnoticed.
      </p>
      <p>
        Only <strong>live</strong> orders are included — <strong>test mail orders</strong> (<code>FID-MAIL-TEST-…</code>)
        and <strong>Stripe/PayPal sandbox</strong> checkouts are excluded. A live Stripe order is included even if PayPal
        is still in sandbox. <strong>Email my cart</strong> inquiries are listed in the same digest.
      </p>
    </div>

    <div className="doc-card">
      <h3>Accessing Order Reminders</h3>
      <ol>
        <li>Go to <strong>Admin Dashboard</strong> → <strong>Order Reminders</strong></li>
        <li>Configure schedule, admin To/CC emails, and optional BCC</li>
        <li>Use <strong>Send Test Reminder Now</strong> to preview the digest immediately</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>How It Works</h3>
      <ol>
        <li><strong>Eligible orders:</strong> Live orders with <code>pending</code> or <code>processing</code> fulfillment status</li>
        <li><strong>Excluded:</strong> Test mail orders, Stripe Test payments, PayPal Sandbox payments (only the method that was used)</li>
        <li><strong>Email my cart:</strong> All current cart inquiries appear in a separate section</li>
        <li><strong>Digest email:</strong> Groups orders by age (7+ days, 3–6 days, 1–2 days, new) with links to Order Management</li>
        <li><strong>Schedule:</strong> Cron-based (e.g. every Monday 9 AM, or daily at 5 PM) — saved in admin settings</li>
      </ol>
    </div>

    <div className="doc-card">
      <h3>Reminder Email Content</h3>
      <p>The automated admin email includes:</p>
      <ul>
        <li>Count of open live orders and urgency breakdown by age</li>
        <li>Order number, customer, total, status, and age for each order</li>
        <li>Link to <strong>Order Management</strong> and instructions for invoices, labels, and status updates</li>
      </ul>
    </div>

    <div className="doc-card important">
      <h3>💡 Best Practices</h3>
      <ul>
        <li>Run <strong>Send Test Reminder Now</strong> after changing schedule or emails</li>
        <li>Use test mail orders when practicing the mail/check flow — they will not appear in reminders</li>
        <li>During busy periods, a daily schedule (morning or end of day) works well</li>
      </ul>
    </div>

    <div className="doc-card warning">
      <h3>⚠️ Important Notes</h3>
      <ul>
        <li>Reminders go to configured admin addresses, not customers</li>
        <li>Test/sandbox orders never appear in the digest, even if still pending</li>
        <li>Shipped, delivered, and cancelled orders are not included</li>
      </ul>
    </div>
  </div>
);

export default AdminDocumentation;

